import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../supabase';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import ViewPolicies from '../student/ViewPolicies';
import ViewContactUs from '../student/ViewContactUs';
import './ParentDashboard.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Create bus icon
let busIconInstance = null;
const getBusIcon = () => {
  if (!busIconInstance) {
    busIconInstance = L.divIcon({
      className: 'custom-marker-icon',
      html: `<div style="
        width: 35px;
        height: 35px;
        background-color: #FF8B3D;
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
      ">🚌</div>`,
      iconSize: [35, 35],
      iconAnchor: [17, 35],
      popupAnchor: [0, -35]
    });
  }
  return busIconInstance;
};

// Create default icon for pickup locations
let defaultIconInstance = null;
const getDefaultIcon = () => {
  if (!defaultIconInstance) {
    defaultIconInstance = L.divIcon({
      className: 'custom-marker-icon',
      html: `<div style="
        width: 30px;
        height: 30px;
        background-color: #3B82F6;
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        color: white;
        font-weight: bold;
      ">📍</div>`,
      iconSize: [30, 30],
      iconAnchor: [15, 30],
      popupAnchor: [0, -30]
    });
  }
  return defaultIconInstance;
};

function ParentDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [parentData, setParentData] = useState(null);
  const [parentDetails, setParentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [children, setChildren] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null); // Selected child for filtering
  
  // Get active tab from URL
  const getTabFromPath = (pathname) => {
    const path = pathname.split('/').pop();
    const validTabs = ['dashboard', 'children', 'homework', 'attendance', 'fees', 'schedule', 'exam-schedule', 'results', 'chat', 'transport', 'meals', 'policies', 'contact-us'];
    return validTabs.includes(path) ? path : 'dashboard';
  };
  
  const [activeTab, setActiveTab] = useState(getTabFromPath(location.pathname));
  const [homeworkData, setHomeworkData] = useState([]);
  const [loadingHomework, setLoadingHomework] = useState(false);
  const [homeworkDate, setHomeworkDate] = useState(new Date().toISOString().split('T')[0]);
  const [filteredHomework, setFilteredHomework] = useState([]);
  const [homeworkCompletionStatus, setHomeworkCompletionStatus] = useState({}); // {homeworkId-childId: {is_completed, completed_at}}
  const [attendanceData, setAttendanceData] = useState([]);
  const [loadingAttendance, setLoadingAttendance] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [scheduleData, setScheduleData] = useState({});
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [schoolTimings, setSchoolTimings] = useState({}); // Map of child_id -> school timings
  const [loadingTimings, setLoadingTimings] = useState(false);
  const [examScheduleData, setExamScheduleData] = useState([]);
  const [examScheduleLoading, setExamScheduleLoading] = useState(false);
  const [examResults, setExamResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [showResultCard, setShowResultCard] = useState(false);
  const [selectedResultCard, setSelectedResultCard] = useState(null);
  const [childrenFees, setChildrenFees] = useState({}); // {childId: [fees]}
  const [feesLoading, setFeesLoading] = useState(false);
  // Chat state
  const [teachers, setTeachers] = useState([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  // Transport state
  const [childrenTransportData, setChildrenTransportData] = useState({}); // {childId: transportData}
  const [transportLoading, setTransportLoading] = useState(false);
  const [busLocations, setBusLocations] = useState({}); // {childId: busLocation}
  const [locationUpdateInterval, setLocationUpdateInterval] = useState(null);
  
  // Meals state
  const [meals, setMeals] = useState([]);
  const [mealsLoading, setMealsLoading] = useState(false);
  const [notices, setNotices] = useState([]);
  const [noticesLoading, setNoticesLoading] = useState(false);

  // Update active tab when URL changes
  useEffect(() => {
    const tab = getTabFromPath(location.pathname);
    setActiveTab(tab);
  }, [location.pathname]);

  // Handle tab change with navigation
  const handleTabChange = (tab) => {
    navigate(`/parent/${tab}`);
    setActiveTab(tab);
  };

  // Child selector component
  const renderChildSelector = () => {
    if (children.length === 0) return null;
    
    return (
      <div className="child-selector-container">
        <label htmlFor="child-select" className="child-selector-label">Select Child:</label>
        <select
          id="child-select"
          value={selectedChild || ''}
          onChange={(e) => setSelectedChild(e.target.value || null)}
          className="child-selector-dropdown"
        >
          <option value="">All Children</option>
          {children.map((child) => (
            <option key={child.id} value={child.id}>
              {child.first_name} {child.last_name} - {child.classes?.class_name || child.class_id} ({child.sections?.section_name || child.section_id})
            </option>
          ))}
        </select>
      </div>
    );
  };
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatGroup, setChatGroup] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [teacherSearchQuery, setTeacherSearchQuery] = useState('');
  const [showSidebar, setShowSidebar] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const chatMessagesEndRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = useRef(null);
  
  // AI Chat state
  const [aiChatMessages, setAiChatMessages] = useState([]);
  const [aiChatInput, setAiChatInput] = useState('');
  const [aiChatLoading, setAiChatLoading] = useState(false);
  const [showApiKeySettings, setShowApiKeySettings] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const aiChatMessagesEndRef = useRef(null);

  useEffect(() => {
    const storedParentData = localStorage.getItem('parentData');
    if (storedParentData) {
      const parsedData = JSON.parse(storedParentData);
      setParentData(parsedData);
      // Fetch parent details and children using email
      if (parsedData.email) {
        fetchParentDetails(parsedData.email);
        fetchChildrenByEmail(parsedData.email);
      }
    } else {
      navigate('/');
    }
    setLoading(false);
  }, [navigate]);


  const fetchParentDetails = async (parentEmail) => {
    try {
      // Fetch parent details from students table (parent info is stored there)
      const { data, error } = await supabase
        .from('students')
        .select('parent_name, parent_email, parent_phone, parent_relationship, parent_address, parent_is_primary, parent_can_pickup, parent_is_emergency_contact, parent_pays_fees')
        .eq('parent_email', parentEmail)
        .limit(1)
        .single();

      if (error) {
        console.error('Error fetching parent details:', error);
        // Try to get from any student record with this parent email
        const { data: studentData } = await supabase
          .from('students')
          .select('parent_name, parent_email, parent_phone, parent_relationship, parent_address, parent_is_primary, parent_can_pickup, parent_is_emergency_contact, parent_pays_fees')
          .eq('parent_email', parentEmail)
          .limit(1);

        if (studentData && studentData.length > 0) {
          setParentDetails(studentData[0]);
        }
      } else {
        setParentDetails(data);
      }
    } catch (error) {
      console.error('Error fetching parent details:', error);
    }
  };

  const fetchChildrenByEmail = async (parentEmail) => {
    if (!parentEmail) {
      setChildren([]);
      return;
    }
    
    try {
      setLoadingChildren(true);
      console.log('Fetching children for parent email:', parentEmail);
      
      // Fetch students first (same logic as AddStudent fetchSiblings)
      const { data: studentsData, error: studentsError } = await supabase
        .from('students')
        .select(`
          id,
          first_name,
          middle_name,
          last_name,
          email,
          class_id,
          section_id,
          date_of_birth,
          gender,
          is_active,
          school_id,
          transport_service_enabled,
          route_id,
          pickup_location_id,
          bus_seat_number
        `)
        .eq('parent_email', parentEmail.trim())
        .eq('is_active', true)
        .order('first_name');
      
      if (studentsError) {
        console.error('Error fetching children:', studentsError);
        setChildren([]);
        return;
      }
      
      if (!studentsData || studentsData.length === 0) {
        console.log('No children found');
        setChildren([]);
        return;
      }
      
      // Get unique school IDs and class IDs
      const schoolIds = [...new Set(studentsData.map(s => s.school_id).filter(Boolean))];
      const classIds = [...new Set(studentsData.map(s => s.class_id).filter(Boolean))];
      
      // Fetch classes
      let classMap = {};
      if (classIds.length > 0) {
        const { data: classesData, error: classesError } = await supabase
          .from('classes')
          .select('id, class_name, sections')
          .in('id', classIds);
        
        if (!classesError && classesData) {
          classesData.forEach(cls => {
            classMap[cls.id] = {
              class_name: cls.class_name,
              sections: cls.sections || []
            };
          });
        }
      }
      
      // Fetch schools
      let schoolMap = {};
      if (schoolIds.length > 0) {
        const { data: schoolsData, error: schoolsError } = await supabase
          .from('schools')
          .select('id, school_name')
          .in('id', schoolIds);
        
        if (!schoolsError && schoolsData) {
          schoolsData.forEach(school => {
            schoolMap[school.id] = school.school_name;
          });
        }
      }
      
      // Combine students with class and school data
      const childrenWithDetails = studentsData.map(student => {
        const classInfo = classMap[student.class_id] || {};
        
        // Get section name from class sections array if available
        let sectionName = student.section_id || 'N/A';
        if (classInfo.sections && Array.isArray(classInfo.sections) && student.section_id) {
          // Try to find section by index (1-based) or by matching name
          const sectionIdNum = parseInt(student.section_id);
          if (!isNaN(sectionIdNum) && sectionIdNum > 0 && sectionIdNum <= classInfo.sections.length) {
            sectionName = classInfo.sections[sectionIdNum - 1];
          } else {
            // Try to find by matching string
            const foundSection = classInfo.sections.find(s => 
              s && (s.toString() === student.section_id.toString() || s.toLowerCase() === student.section_id.toString().toLowerCase())
            );
            if (foundSection) {
              sectionName = foundSection;
            }
          }
        }
        
        return {
          ...student,
          classes: {
            class_name: classInfo.class_name || 'N/A'
          },
          sections: {
            section_name: sectionName
          },
          schools: {
            school_name: schoolMap[student.school_id] || 'N/A'
          }
        };
      });
      
      console.log('Found children:', childrenWithDetails);
      setChildren(childrenWithDetails);
    } catch (err) {
      console.error('Error:', err);
      setChildren([]);
    } finally {
      setLoadingChildren(false);
    }
  };

  const fetchParentNotices = useCallback(async () => {
    if (!children || children.length === 0) {
      setNotices([]);
      return;
    }

    try {
      setNoticesLoading(true);
      const schoolIds = [...new Set(children.map(child => child.school_id).filter(Boolean))];

      if (schoolIds.length === 0) {
        setNotices([]);
        return;
      }

      const { data, error } = await supabase
        .from('notices')
        .select('*')
        .in('school_id', schoolIds)
        .eq('target_audience', 'parents')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching parent notices:', error);
        setNotices([]);
        return;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const activeNotices = (data || []).filter(notice => {
        if (notice.status && notice.status !== 'sent') {
          return false;
        }

        if (!notice.expiry_date) return true;

        const expiryDate = new Date(`${notice.expiry_date}T00:00:00`);
        return expiryDate >= today;
      });

      setNotices(activeNotices);
    } catch (err) {
      console.error('Error fetching parent notices:', err);
      setNotices([]);
    } finally {
      setNoticesLoading(false);
    }
  }, [children]);

  const fetchHomeworkForChildren = useCallback(async () => {
    if (!children || children.length === 0) {
      setHomeworkData([]);
      return;
    }
    
    try {
      setLoadingHomework(true);
      console.log('Fetching homework for children:', children);
      
      // Collect all unique school_id, class_id, and section_id combinations
      const homeworkQueries = children.map(child => ({
        school_id: child.school_id,
        class_id: child.class_id,
        section_name: child.section_id // Note: homework table uses section_name, not section_id
      }));
      
      // Remove duplicates
      const uniqueQueries = homeworkQueries.filter((query, index, self) =>
        index === self.findIndex(q => 
          q.school_id === query.school_id &&
          q.class_id === query.class_id &&
          q.section_name === query.section_name
        )
      );
      
      // Fetch homework for each unique combination
      const allHomework = [];
      for (const query of uniqueQueries) {
        if (!query.school_id || !query.class_id || !query.section_name) continue;
        
        const { data, error } = await supabase
          .from('homework')
          .select(`
            *,
            teachers(first_name, last_name)
          `)
          .eq('school_id', query.school_id)
          .eq('class_id', query.class_id)
          .eq('section_name', query.section_name.toString())
          .order('assigned_date', { ascending: false });
        
        if (error) {
          console.error('Error fetching homework:', error);
          continue;
        }
        
        if (data && data.length > 0) {
          // Add child information to each homework item
          const childrenInThisClass = children.filter(child =>
            child.school_id === query.school_id &&
            child.class_id === query.class_id &&
            child.section_id?.toString() === query.section_name.toString()
          );
          
          data.forEach(hw => {
            childrenInThisClass.forEach(child => {
              allHomework.push({
                ...hw,
                child_id: child.id,
                child_name: `${child.first_name} ${child.middle_name ? child.middle_name + ' ' : ''}${child.last_name}`,
                child_class: child.classes?.class_name || 'N/A',
                child_section: child.sections?.section_name || child.section_id || 'N/A'
              });
            });
          });
        }
      }
      
      console.log('All homework fetched:', allHomework);
      setHomeworkData(allHomework);
      
      // Fetch completion status for all homework
      if (allHomework.length > 0 && children.length > 0) {
        const childIds = children.map(c => c.id);
        const homeworkIds = [...new Set(allHomework.map(h => h.id))];
        
        const { data: completionData, error: completionError } = await supabase
          .from('homework_completion')
          .select('*')
          .in('student_id', childIds)
          .in('homework_id', homeworkIds);

        if (!completionError && completionData) {
          const statusMap = {};
          completionData.forEach(completion => {
            const key = `${completion.homework_id}-${completion.student_id}`;
            statusMap[key] = {
              is_completed: completion.is_completed,
              completed_at: completion.completed_at
            };
          });
          setHomeworkCompletionStatus(statusMap);
        }
      }
    } catch (err) {
      console.error('Error fetching homework:', err);
      setHomeworkData([]);
      setFilteredHomework([]);
    } finally {
      setLoadingHomework(false);
    }
  }, [children]);

  const filterHomeworkByDate = (homework, selectedDate) => {
    let filtered = homework.filter(hw => hw.assigned_date === selectedDate);
    // Filter by selected child if one is selected
    if (selectedChild) {
      const selectedChildId = String(selectedChild);
      filtered = filtered.filter(hw => {
        const hwChildId = String(hw.child_id || hw.student_id || '');
        const matches = hwChildId === selectedChildId;
        console.log(`Comparing: hw.child_id=${hw.child_id}, selectedChild=${selectedChild}, matches=${matches}`);
        return matches;
      });
    }
    setFilteredHomework(filtered);
    console.log(`Filtered homework for ${selectedDate}, selectedChild: ${selectedChild}, filtered count: ${filtered.length}, total: ${homework.length}`);
  };

  const navigateHomeworkDate = (direction) => {
    const currentDate = new Date(homeworkDate);
    
    if (direction === 'prev') {
      currentDate.setDate(currentDate.getDate() - 1);
    } else if (direction === 'next') {
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    const newDate = currentDate.toISOString().split('T')[0];
    setHomeworkDate(newDate);
    filterHomeworkByDate(homeworkData, newDate);
  };

  useEffect(() => {
    if (homeworkData.length > 0) {
      filterHomeworkByDate(homeworkData, homeworkDate);
    }
  }, [homeworkDate]);

  useEffect(() => {
    if (activeTab === 'exam-schedule' && children.length > 0) {
      fetchExamSchedule();
    }
  }, [activeTab, children]);

  useEffect(() => {
    if (activeTab === 'results' && children.length > 0) {
      fetchExamResults();
    }
  }, [activeTab, children]);

  useEffect(() => {
    if (activeTab === 'fees' && children.length > 0) {
      fetchChildrenFees();
    }
  }, [activeTab, children]);

  const fetchChildrenFees = async () => {
    if (!children || children.length === 0) return;
    
    try {
      setFeesLoading(true);
      const feesByChild = {};
      
      // Fetch fees for each child
      for (const child of children) {
        if (!child.school_id || !child.class_id) continue;
        
        try {
          // Get today's date
          const today = new Date();
          const todayStr = today.toISOString().split('T')[0];
          
          // Fetch class fees for default amounts
          const { data: classFeesData, error: classFeesError } = await supabase
            .from('class_fees')
            .select('*')
            .eq('school_id', child.school_id)
            .eq('class_id', child.class_id)
            .eq('is_active', true)
            .single();

          if (classFeesError && classFeesError.code !== 'PGRST116') {
            console.error(`Error fetching class fees for child ${child.id}:`, classFeesError);
            continue;
          }

          if (!classFeesData) continue;

          // Fetch fee schedule for the child's class
          const { data: feeScheduleData } = await supabase
            .from('fee_schedule')
            .select('*')
            .eq('school_id', child.school_id)
            .eq('class_id', child.class_id)
            .eq('is_active', true)
            .order('fee_date', { ascending: true });

          // Fetch additional fees for the child's class
          const { data: additionalFeesData } = await supabase
            .from('additional_class_fees')
            .select('*')
            .eq('school_id', child.school_id)
            .eq('class_id', child.class_id)
            .eq('is_active', true);
          
          // Calculate additional fees
          const monthlyAdditionalFees = additionalFeesData
            ?.filter(fee => fee.period === 'monthly')
            .reduce((sum, fee) => sum + (parseFloat(fee.amount) || 0), 0) || 0;
          
          const oneTimeAdditionalFees = additionalFeesData
            ?.filter(fee => fee.period === 'one_time')
            .reduce((sum, fee) => sum + (parseFloat(fee.amount) || 0), 0) || 0;

          // Fetch academic year
          const { data: academicYearData } = await supabase
            .from('academic_years')
            .select('*')
            .eq('school_id', child.school_id)
            .eq('is_active', true)
            .single();

          let academicYearStart, academicYearEnd;
          
          if (academicYearData) {
            academicYearStart = new Date(academicYearData.start_date);
            academicYearEnd = new Date(academicYearData.end_date);
          } else {
            if (classFeesData.first_fee_date) {
              const firstFeeDate = new Date(classFeesData.first_fee_date);
              academicYearStart = new Date(firstFeeDate.getFullYear(), firstFeeDate.getMonth(), 1);
              academicYearEnd = new Date(firstFeeDate.getFullYear() + 1, firstFeeDate.getMonth() - 1, 0);
            } else {
              const today = new Date();
              const currentYear = today.getFullYear();
              const currentMonth = today.getMonth();
              
              if (currentMonth >= 7) {
                academicYearStart = new Date(currentYear, 7, 1);
                academicYearEnd = new Date(currentYear + 1, 6, 31);
              } else {
                academicYearStart = new Date(currentYear - 1, 7, 1);
                academicYearEnd = new Date(currentYear, 6, 31);
              }
            }
          }
          
          const monthlyFees = [];
          let currentDate = new Date(academicYearStart);
          let isFirstMonth = true;
          
          while (currentDate <= academicYearEnd) {
            const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
            const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
            
            // Check if there's a scheduled fee for this month
            const scheduledFee = feeScheduleData?.find(fee => {
              const feeDate = new Date(fee.fee_date);
              return feeDate.getMonth() === currentDate.getMonth() && 
                     feeDate.getFullYear() === currentDate.getFullYear();
            });

            // Calculate period and due date
            let periodStart = monthStart;
            let periodEnd = monthEnd;
            
            if (classFeesData.first_fee_date) {
              const firstFeeDate = new Date(classFeesData.first_fee_date);
              const expiryDuration = classFeesData.expiry_duration || 30;
              
              if (currentDate.getMonth() === firstFeeDate.getMonth() && 
                  currentDate.getFullYear() === firstFeeDate.getFullYear()) {
                periodStart = new Date(firstFeeDate);
                periodEnd = new Date(firstFeeDate);
                periodEnd.setDate(periodEnd.getDate() + expiryDuration - 1);
              } else {
                const monthsFromStart = (currentDate.getFullYear() - firstFeeDate.getFullYear()) * 12 + 
                                       (currentDate.getMonth() - firstFeeDate.getMonth());
                
                switch (classFeesData.fee_type) {
                  case 'monthly':
                    periodStart = new Date(firstFeeDate);
                    periodStart.setMonth(periodStart.getMonth() + monthsFromStart);
                    periodEnd = new Date(periodStart);
                    periodEnd.setDate(periodEnd.getDate() + expiryDuration - 1);
                    break;
                  case 'quarterly':
                    periodStart = new Date(firstFeeDate);
                    periodStart.setMonth(periodStart.getMonth() + (monthsFromStart * 3));
                    periodEnd = new Date(periodStart);
                    periodEnd.setDate(periodEnd.getDate() + expiryDuration - 1);
                    break;
                  case 'semester':
                    periodStart = new Date(firstFeeDate);
                    periodStart.setMonth(periodStart.getMonth() + (monthsFromStart * 6));
                    periodEnd = new Date(periodStart);
                    periodEnd.setDate(periodEnd.getDate() + expiryDuration - 1);
                    break;
                  case 'annual':
                    if (monthsFromStart === 0) {
                      periodStart = new Date(firstFeeDate);
                      periodEnd = new Date(firstFeeDate);
                      periodEnd.setDate(periodEnd.getDate() + expiryDuration - 1);
                    } else {
                      currentDate.setMonth(currentDate.getMonth() + 1);
                      continue;
                    }
                    break;
                }
              }
            }

            const baseFeeAmount = scheduledFee ? scheduledFee.amount : classFeesData.amount;
            const oneTimeFee = isFirstMonth ? oneTimeAdditionalFees : 0;
            const finalFeeAmount = baseFeeAmount + monthlyAdditionalFees + oneTimeFee;
            
            const finalCurrency = scheduledFee ? scheduledFee.currency : classFeesData.currency || 'PKR';
            const expiryDuration = classFeesData.expiry_duration || 30;
            const finalDueDate = new Date(periodStart);
            finalDueDate.setDate(finalDueDate.getDate() + expiryDuration);

            const periodStartStr = periodStart.toISOString().split('T')[0];
            const periodEndStr = periodEnd.toISOString().split('T')[0];
            
            if (todayStr >= periodStartStr && todayStr <= periodEndStr) {
              monthlyFees.push({
                month: currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
                periodStart: periodStart,
                periodEnd: periodEnd,
                dueDate: finalDueDate,
                feeAmount: finalFeeAmount,
                baseFeeAmount: baseFeeAmount,
                additionalFees: monthlyAdditionalFees + oneTimeFee,
                currency: finalCurrency,
                feeType: classFeesData.fee_type || 'monthly',
                isScheduled: !!scheduledFee,
                description: scheduledFee?.description || '',
                isDueNow: true,
                isPaid: false
              });
            }

            if (isFirstMonth) {
              isFirstMonth = false;
            }

            currentDate.setMonth(currentDate.getMonth() + 1);
          }

          // Fetch payment status from fee_records
          if (monthlyFees.length > 0) {
            const feePeriods = monthlyFees.map(fee => {
              const monthParts = fee.month ? fee.month.split(' ') : ['', ''];
              const monthName = monthParts[0] || '';
              const year = monthParts[1] || new Date().getFullYear().toString();
              const monthMap = {
                'January': '01', 'February': '02', 'March': '03', 'April': '04',
                'May': '05', 'June': '06', 'July': '07', 'August': '08',
                'September': '09', 'October': '10', 'November': '11', 'December': '12'
              };
              const monthNum = monthMap[monthName] || String(new Date().getMonth() + 1).padStart(2, '0');
              return `${year}-${monthNum}`;
            });
            
            const { data: feeRecordsData } = await supabase
              .from('fee_records')
              .select('*')
              .eq('school_id', child.school_id)
              .eq('class_id', child.class_id)
              .eq('section_id', child.section_id)
              .eq('student_id', child.id)
              .in('fee_period', feePeriods);
            
            const paymentStatusMap = {};
            if (feeRecordsData) {
              feeRecordsData.forEach(record => {
                paymentStatusMap[record.fee_period] = record.is_paid;
              });
            }
            
            monthlyFees.forEach(fee => {
              const monthParts = fee.month ? fee.month.split(' ') : ['', ''];
              const monthName = monthParts[0] || '';
              const year = monthParts[1] || new Date().getFullYear().toString();
              const monthMap = {
                'January': '01', 'February': '02', 'March': '03', 'April': '04',
                'May': '05', 'June': '06', 'July': '07', 'August': '08',
                'September': '09', 'October': '10', 'November': '11', 'December': '12'
              };
              const monthNum = monthMap[monthName] || String(new Date().getMonth() + 1).padStart(2, '0');
              const feePeriod = `${year}-${monthNum}`;
              fee.isPaid = paymentStatusMap[feePeriod] || false;
            });
          }

          feesByChild[child.id] = monthlyFees;
        } catch (err) {
          console.error(`Error fetching fees for child ${child.id}:`, err);
        }
      }
      
      setChildrenFees(feesByChild);
    } catch (err) {
      console.error('Error fetching children fees:', err);
      setChildrenFees({});
    } finally {
      setFeesLoading(false);
    }
  };

  const fetchExamResults = async () => {
    if (!children || children.length === 0) return;
    
    try {
      setResultsLoading(true);
      
      // Get all child IDs
      const childIds = children.map(child => child.id);
      
      if (childIds.length === 0) {
        setExamResults([]);
        return;
      }

      // Fetch published exam marks for all children
      const { data: marksData, error: marksError } = await supabase
        .from('exam_marks')
        .select(`
          id,
          exam_schedule_id,
          student_id,
          subject_name,
          obtained_marks,
          total_marks,
          grade,
          percentage,
          remarks,
          created_at
        `)
        .in('student_id', childIds)
        .eq('is_published', true)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (marksError) throw marksError;

      // Fetch exam schedule details
      const examIds = [...new Set(marksData.map(m => m.exam_schedule_id))];
      let examsData = [];
      
      if (examIds.length > 0) {
        const { data: exams, error: examsError } = await supabase
          .from('exam_schedule')
          .select('id, title, type, start_date, end_date, is_published')
          .in('id', examIds)
          .eq('is_published', true)
          .eq('is_active', true);

        if (examsError) throw examsError;
        examsData = exams || [];
      }

      // Fetch class information for children
      const classIds = [...new Set(children.map(c => c.class_id).filter(Boolean))];
      let classesData = [];
      
      if (classIds.length > 0) {
        const { data: classes, error: classesError } = await supabase
          .from('classes')
          .select('id, class_name')
          .in('id', classIds);

        if (!classesError) {
          classesData = classes || [];
        }
      }

      // Combine marks with exam details and group by child and exam
      const resultsByChild = {};
      
      marksData.forEach(mark => {
        const exam = examsData.find(e => e.id === mark.exam_schedule_id);
        const childId = mark.student_id;
        
        if (!resultsByChild[childId]) {
          const child = children.find(c => c.id === childId);
          if (child) {
            const childClass = classesData.find(c => c.id === child.class_id);
            resultsByChild[childId] = {
              child: {
                ...child,
                classes: childClass || null
              },
              exams: {}
            };
          }
        }
        
        if (resultsByChild[childId]) {
          const examId = mark.exam_schedule_id;
          if (!resultsByChild[childId].exams[examId]) {
            resultsByChild[childId].exams[examId] = {
              exam: exam || null,
              marks: []
            };
          }
          
          resultsByChild[childId].exams[examId].marks.push(mark);
        }
      });

      // Convert to array format
      const formattedResults = Object.values(resultsByChild).map(childData => ({
        child: childData.child,
        examGroups: Object.values(childData.exams)
      }));

      setExamResults(formattedResults);
    } catch (error) {
      console.error('Error fetching exam results:', error);
      setExamResults([]);
    } finally {
      setResultsLoading(false);
    }
  };

  const fetchExamSchedule = async () => {
    if (!children || children.length === 0) {
      setExamScheduleData([]);
      return;
    }
    
    try {
      setExamScheduleLoading(true);
      console.log('Fetching exam schedule for children:', children);
      
      // Collect all unique school_id and class_id combinations
      const examQueries = children.map(child => ({
        school_id: child.school_id,
        class_id: child.class_id,
        section_id: child.section_id
      }));
      
      // Remove duplicates
      const uniqueQueries = examQueries.filter((query, index, self) =>
        index === self.findIndex(q => 
          q.school_id === query.school_id &&
          q.class_id === query.class_id &&
          q.section_id === query.section_id
        )
      );
      
      // Fetch exams for each unique combination
      const allExams = [];
      for (const query of uniqueQueries) {
        if (!query.school_id || !query.class_id) continue;
        
        let examQuery = supabase
          .from('exam_schedule')
          .select('*')
          .eq('school_id', query.school_id)
          .eq('class_id', query.class_id)
          .eq('is_active', true)
          .order('start_date', { ascending: true });
        
        const { data: exams, error: examError } = await examQuery;
        
        if (examError) {
          console.error('Error fetching exam schedule:', examError);
          continue;
        }
        
        if (exams && exams.length > 0) {
          // Filter exams based on section
          let filteredExams = exams;
          if (query.section_id) {
            filteredExams = exams.filter(exam => 
              !exam.section_name || exam.section_name === query.section_id.toString()
            );
          }
          
          // Add child information to each exam
          const childrenInThisClass = children.filter(child =>
            child.school_id === query.school_id &&
            child.class_id === query.class_id &&
            (!query.section_id || child.section_id?.toString() === query.section_id.toString())
          );
          
          filteredExams.forEach(exam => {
            childrenInThisClass.forEach(child => {
              allExams.push({
                ...exam,
                child_id: child.id,
                child_name: `${child.first_name} ${child.middle_name ? child.middle_name + ' ' : ''}${child.last_name}`,
                child_class: child.classes?.class_name || 'N/A',
                child_section: child.sections?.section_name || child.section_id || 'N/A'
              });
            });
          });
        }
      }
      
      // Remove duplicates and sort by date
      const uniqueExams = allExams.filter((exam, index, self) =>
        index === self.findIndex(e => 
          e.id === exam.id && e.child_id === exam.child_id
        )
      );
      
      console.log('All exams fetched:', uniqueExams);
      setExamScheduleData(uniqueExams);
    } catch (error) {
      console.error('Error fetching exam schedule:', error);
      setExamScheduleData([]);
    } finally {
      setExamScheduleLoading(false);
    }
  };

  const fetchAttendanceForChildren = useCallback(async () => {
    if (!children || children.length === 0) {
      setAttendanceData([]);
      return;
    }
    
    try {
      setLoadingAttendance(true);
      console.log('Fetching attendance for children:', children);
      
      // Calculate date range for the selected month
      const startDate = new Date(selectedYear, selectedMonth, 1).toISOString().split('T')[0];
      const endDate = new Date(selectedYear, selectedMonth + 1, 0).toISOString().split('T')[0];
      
      console.log('Date range:', { startDate, endDate });
      
      // Get all student IDs
      const studentIds = children.map(child => child.id);
      
      if (studentIds.length === 0) {
        setAttendanceData([]);
        return;
      }
      
      // Fetch attendance records for all children
      const { data: records, error: recordsError } = await supabase
        .from('attendance')
        .select('*')
        .in('student_id', studentIds)
        .gte('attendance_date', startDate)
        .lte('attendance_date', endDate)
        .order('attendance_date', { ascending: false });
      
      if (recordsError) {
        console.error('Error fetching attendance records:', recordsError);
        setAttendanceData([]);
        return;
      }
      
      console.log('Attendance records fetched:', records);
      
      // Fetch teacher data
      const teacherIds = [...new Set(records.map(record => record.teacher_id).filter(Boolean))];
      let teachers = [];
      
      if (teacherIds.length > 0) {
        const { data: teachersData, error: teachersError } = await supabase
          .from('teachers')
          .select('*')
          .in('id', teacherIds);
        
        if (!teachersError && teachersData) {
          teachers = teachersData;
        }
      }
      
      // Create maps for efficient lookup
      const childrenMap = {};
      children.forEach(child => {
        childrenMap[child.id] = child;
      });
      
      const teachersMap = {};
      teachers.forEach(teacher => {
        teachersMap[teacher.id] = teacher;
      });
      
      // Enrich records with child and teacher data
      const enrichedRecords = (records || []).map(record => {
        const child = childrenMap[record.student_id];
        return {
          ...record,
          child: child,
          child_id: record.student_id,
          child_name: child 
            ? `${child.first_name} ${child.middle_name ? child.middle_name + ' ' : ''}${child.last_name}`
            : 'Unknown',
          child_class: child?.classes?.class_name || 'N/A',
          child_section: child?.sections?.section_name || child?.section_id || 'N/A',
          teacher: teachersMap[record.teacher_id]
        };
      });
      
      console.log('Enriched attendance records:', enrichedRecords);
      setAttendanceData(enrichedRecords);
    } catch (err) {
      console.error('Error fetching attendance:', err);
      setAttendanceData([]);
    } finally {
      setLoadingAttendance(false);
    }
  }, [children, selectedMonth, selectedYear]);

  useEffect(() => {
    if (activeTab === 'homework' && children.length > 0) {
      fetchHomeworkForChildren();
    }
  }, [activeTab, children, fetchHomeworkForChildren]);

  useEffect(() => {
    if (children.length > 0) {
      fetchParentNotices();
    } else {
      setNotices([]);
    }
  }, [children, fetchParentNotices]);

  // Re-filter homework when selectedChild changes
  useEffect(() => {
    if (homeworkData.length > 0 && activeTab === 'homework') {
      filterHomeworkByDate(homeworkData, homeworkDate);
    }
  }, [selectedChild, homeworkDate, homeworkData, activeTab]);

  const fetchSchoolTimingsForChildren = useCallback(async () => {
    if (!children || children.length === 0) {
      setSchoolTimings({});
      return;
    }
    
    try {
      setLoadingTimings(true);
      const timingsByChild = {};
      
      // Get unique school IDs
      const schoolIds = [...new Set(children.map(child => child.school_id).filter(Boolean))];
      
      // Fetch timings for each school
      for (const schoolId of schoolIds) {
        const { data, error } = await supabase
          .from('school_timings')
          .select('*')
          .eq('school_id', schoolId)
          .is('class_id', null) // School-wide timings
          .order('created_at', { ascending: false })
          .limit(1);
        
        if (!error && data && data.length > 0) {
          // Assign this timing to all children from this school
          children.forEach(child => {
            if (child.school_id === schoolId) {
              timingsByChild[child.id] = data[0];
            }
          });
        }
      }
      
      console.log('School timings fetched:', timingsByChild);
      setSchoolTimings(timingsByChild);
    } catch (err) {
      console.error('Error fetching school timings:', err);
      setSchoolTimings({});
    } finally {
      setLoadingTimings(false);
    }
  }, [children]);

  // Helper functions for time calculations (matching student dashboard)
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    
    if (timeString === null || timeString === undefined) return 'N/A';
    
    const timeStr = String(timeString).trim();
    if (!timeStr) return 'N/A';
    
    if (timeStr.match(/^\d{1,2}:\d{2}$/)) {
      return timeStr;
    }
    
    if (timeStr.match(/^\d{1,2}$/)) {
      return timeStr.padStart(2, '0') + ':00';
    }
    
    return timeStr;
  };

  const addMinutes = (timeString, minutes) => {
    let formattedTime = timeString;
    if (!timeString.includes(':')) {
      formattedTime = timeString.padStart(2, '0') + ':00';
    }
    
    const [hours, mins] = formattedTime.split(':').map(Number);
    const totalMinutes = hours * 60 + mins + minutes;
    const newHours = Math.floor(totalMinutes / 60);
    const newMins = totalMinutes % 60;
    return `${newHours.toString().padStart(2, '0')}:${newMins.toString().padStart(2, '0')}`;
  };

  const calculatePeriodTimingsForPeriod = (periodNumber, timings) => {
    if (!timings) {
      return { start: 'N/A', end: 'N/A', breakAfter: null };
    }
    
    const startTime = formatTime(timings.start_time);
    const periodDuration = timings.period_time || 40;
    const breaks = timings.breaks || [];
    
    let periodStartTime = startTime;
    
    for (let i = 1; i < periodNumber; i++) {
      const periodIEnd = addMinutes(periodStartTime, periodDuration);
      
      const breakAfter = breaks.find(breakItem => {
        const breakStart = formatTime(breakItem.startTime);
        return breakStart === periodIEnd;
      });
      
      if (breakAfter) {
        periodStartTime = formatTime(breakAfter.endTime);
      } else {
        periodStartTime = periodIEnd;
      }
    }
    
    const periodEndTime = addMinutes(periodStartTime, periodDuration);
    
    const breakAfter = breaks.find(breakItem => {
      const breakStart = formatTime(breakItem.startTime);
      return breakStart === periodEndTime;
    });
    
    return {
      start: periodStartTime,
      end: periodEndTime,
      breakAfter: breakAfter
    };
  };

  const fetchScheduleForChildren = useCallback(async () => {
    if (!children || children.length === 0) {
      setScheduleData({});
      return;
    }
    
    try {
      setLoadingSchedule(true);
      console.log('Fetching schedules for children:', children);
      
      const schedulesByChild = {};
      
      // Fetch schedule for each child
      for (const child of children) {
        if (!child.school_id || !child.class_id || !child.section_id) {
          continue;
        }
        
        try {
          // Fetch ALL schedules for this child's class and section (all semesters)
          const { data, error } = await supabase
            .from('class_schedules')
            .select('*')
            .eq('school_id', child.school_id)
            .eq('class_id', child.class_id)
            .eq('section_name', child.section_id.toString())
            .order('day')
            .order('period');
          
          if (error) {
            console.error(`Error fetching schedule for child ${child.id}:`, error);
            // Try fallback with section name matching
            const { data: fallbackData, error: fallbackError } = await supabase
              .from('class_schedules')
              .select('*')
              .eq('school_id', child.school_id)
              .eq('class_id', child.class_id)
              .ilike('section_name', `%${child.section_id}%`)
              .order('day')
              .order('period');
            
            if (!fallbackError && fallbackData && fallbackData.length > 0) {
              // Group by semester
              const schedulesGrouped = {};
              fallbackData.forEach(entry => {
                const semester = entry.semester || '1st Semester';
                const semesterName = entry.semester_name || semester;
                
                if (!schedulesGrouped[semester]) {
                  schedulesGrouped[semester] = {
                    semester: semester,
                    semester_name: semesterName,
                    schedule: {},
                    created_at: entry.created_at,
                    updated_at: entry.updated_at
                  };
                }
                
                const key = `${entry.day}_${entry.period}`;
                schedulesGrouped[semester].schedule[key] = {
                  subject_name: entry.subject_name,
                  teacher_name: entry.teacher_name,
                  teacher_id: entry.teacher_id
                };
              });
              
              // Sort semesters: "1st Semester" first, then others alphabetically
              const sortedSemesters = Object.keys(schedulesGrouped).sort((a, b) => {
                if (a === '1st Semester') return -1;
                if (b === '1st Semester') return 1;
                return a.localeCompare(b);
              });
              
              const orderedSchedules = {};
              sortedSemesters.forEach(sem => {
                orderedSchedules[sem] = schedulesGrouped[sem];
              });
              
              // For backward compatibility, use first semester as default
              const firstSemester = sortedSemesters[0];
              const firstScheduleObject = firstSemester ? schedulesGrouped[firstSemester].schedule : {};
              
              schedulesByChild[child.id] = {
                child: child,
                schedule: fallbackData,
                scheduleObject: firstScheduleObject,
                schedulesBySemester: orderedSchedules
              };
            }
            continue;
          }
          
          if (data && data.length > 0) {
            // Group schedules by semester
            const schedulesGrouped = {};
            
            data.forEach(entry => {
              const semester = entry.semester || '1st Semester';
              const semesterName = entry.semester_name || semester;
              
              if (!schedulesGrouped[semester]) {
                schedulesGrouped[semester] = {
                  semester: semester,
                  semester_name: semesterName,
                  schedule: {},
                  created_at: entry.created_at,
                  updated_at: entry.updated_at
                };
              }
              
              const key = `${entry.day}_${entry.period}`;
              schedulesGrouped[semester].schedule[key] = {
                subject_name: entry.subject_name,
                teacher_name: entry.teacher_name,
                teacher_id: entry.teacher_id
              };
              
              // Update timestamps if this entry is newer
              if (entry.updated_at && (!schedulesGrouped[semester].updated_at || entry.updated_at > schedulesGrouped[semester].updated_at)) {
                schedulesGrouped[semester].updated_at = entry.updated_at;
              }
            });
            
            // Sort semesters: "1st Semester" first, then others alphabetically
            const sortedSemesters = Object.keys(schedulesGrouped).sort((a, b) => {
              if (a === '1st Semester') return -1;
              if (b === '1st Semester') return 1;
              return a.localeCompare(b);
            });
            
            const orderedSchedules = {};
            sortedSemesters.forEach(sem => {
              orderedSchedules[sem] = schedulesGrouped[sem];
            });
            
            // For backward compatibility, use first semester as default scheduleObject
            const firstSemester = sortedSemesters[0];
            const firstScheduleObject = firstSemester ? schedulesGrouped[firstSemester].schedule : {};
            
            schedulesByChild[child.id] = {
              child: child,
              schedule: data,
              scheduleObject: firstScheduleObject,
              schedulesBySemester: orderedSchedules
            };
          }
        } catch (err) {
          console.error(`Error processing schedule for child ${child.id}:`, err);
        }
      }
      
      console.log('Schedules fetched:', schedulesByChild);
      setScheduleData(schedulesByChild);
    } catch (err) {
      console.error('Error fetching schedules:', err);
      setScheduleData({});
    } finally {
      setLoadingSchedule(false);
    }
  }, [children]);

  useEffect(() => {
    if (activeTab === 'homework' && children.length > 0) {
      fetchHomeworkForChildren();
    }
  }, [activeTab, children, fetchHomeworkForChildren]);

  useEffect(() => {
    if (activeTab === 'attendance' && children.length > 0) {
      fetchAttendanceForChildren();
    }
  }, [activeTab, children, fetchAttendanceForChildren]);

  useEffect(() => {
    if (activeTab === 'schedule' && children.length > 0) {
      fetchScheduleForChildren();
      fetchSchoolTimingsForChildren();
    }
  }, [activeTab, children, fetchScheduleForChildren, fetchSchoolTimingsForChildren]);

  // Fetch transport data when transport tab is active
  useEffect(() => {
    if (activeTab === 'transport' && children.length > 0) {
      fetchTransportDataForChildren();
    }
  }, [activeTab, children]);

  // Fetch meals when meals tab is active
  useEffect(() => {
    if (activeTab === 'meals' && children.length > 0) {
      fetchMealsForChildren();
    }
  }, [activeTab, children]);

  // Set up location update interval when transport data is available
  useEffect(() => {
    if (activeTab === 'transport' && Object.keys(childrenTransportData).length > 0) {
      // Initial location update
      updateBusLocations();
      
      // Set up interval to update bus locations every 10 seconds
      const interval = setInterval(() => {
        updateBusLocations();
      }, 10000);
      setLocationUpdateInterval(interval);
      
      return () => {
        if (interval) clearInterval(interval);
      };
    } else {
      if (locationUpdateInterval) {
        clearInterval(locationUpdateInterval);
        setLocationUpdateInterval(null);
      }
    }
  }, [activeTab, childrenTransportData]);

  // Helper functions for schedule display (matching student dashboard)
  const getWorkingDays = () => {
    // Always use all 7 days as potential working days
    return ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  };

  const getPeriods = (scheduleObject) => {
    if (!scheduleObject) return [];
    
    const periods = new Set();
    Object.keys(scheduleObject).forEach(key => {
      const [day, period] = key.split('_');
      periods.add(parseInt(period));
    });
    
    return Array.from(periods).sort((a, b) => a - b);
  };

  const getDayName = (day) => {
    const dayNames = {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday'
    };
    return dayNames[day] || day;
  };

  const handleLogout = () => {
    localStorage.removeItem('parentData');
    localStorage.removeItem('parentAuthenticated');
    navigate('/');
  };

  // Chat Functions
  const fetchTeachersForChildren = async () => {
    if (!children || children.length === 0 || !parentData) {
      setTeachers([]);
      return;
    }

    try {
      setTeachersLoading(true);
      const teachersMap = {};
      
      // Get all unique teachers from all children's classes
      for (const child of children) {
        if (!child.school_id || !child.class_id || !child.section_id) continue;
        
        const classId = typeof child.class_id === 'string' ? parseInt(child.class_id, 10) : child.class_id;
        const sectionId = String(child.section_id).trim();

        // Fetch teachers from class_schedules
        const { data: scheduleTeachers } = await supabase
          .from('class_schedules')
          .select('teacher_id, teacher_name, subject_name')
          .eq('school_id', child.school_id)
          .eq('class_id', classId)
          .eq('section_name', sectionId);

        if (scheduleTeachers && scheduleTeachers.length > 0) {
          scheduleTeachers.forEach(schedule => {
            if (schedule.teacher_id) {
              const teacherId = schedule.teacher_id;
              if (!teachersMap[teacherId]) {
                teachersMap[teacherId] = {
                  id: teacherId,
                  first_name: schedule.teacher_name?.split(' ')[0] || 'Teacher',
                  last_name: schedule.teacher_name?.split(' ').slice(1).join(' ') || '',
                  subjects: [],
                  children: []
                };
              }
              if (schedule.subject_name && !teachersMap[teacherId].subjects.includes(schedule.subject_name)) {
                teachersMap[teacherId].subjects.push(schedule.subject_name);
              }
              if (!teachersMap[teacherId].children.find(c => c.id === child.id)) {
                teachersMap[teacherId].children.push({
                  id: child.id,
                  name: `${child.first_name} ${child.last_name}`,
                  class: child.class_name,
                  section: child.section_id
                });
              }
            }
          });
        }
      }

      const teachersList = Object.values(teachersMap);
      
      // Add admin (school) to the list
      const adminContact = {
        id: 'admin',
        first_name: 'School',
        last_name: 'Admin',
        isAdmin: true
      };
      
      setTeachers([adminContact, ...teachersList]);
      await calculateUnreadCounts([adminContact, ...teachersList]);
    } catch (err) {
      console.error('Error fetching teachers:', err);
      setTeachers([]);
    } finally {
      setTeachersLoading(false);
    }
  };

  const calculateUnreadCounts = async (teachersList) => {
    if (!parentData || !teachersList || teachersList.length === 0) return;

    const counts = {};
    const schoolId = parentData.school_id || children[0]?.school_id;
    
    for (const teacher of teachersList) {
      try {
        // Handle admin separately
        if (teacher.isAdmin) {
          const { data: groups } = await supabase
            .from('chat_groups')
            .select('id')
            .eq('school_id', schoolId)
            .eq('group_type', 'custom')
            .ilike('name', `%Admin Chat%`);

          if (groups && groups.length > 0) {
            for (const group of groups) {
              const { data: members } = await supabase
                .from('chat_group_members')
                .select('*')
                .eq('group_id', group.id);

              const hasParent = members?.some(m => m.user_type === 'parent' && m.user_id === parentData.id?.toString());
              const hasAdmin = members?.some(m => m.user_type === 'school');

              if (hasParent && hasAdmin) {
                const lastReadKey = `lastRead_${parentData.id}_admin`;
                const lastReadTimestamp = localStorage.getItem(lastReadKey);

                let query = supabase
                  .from('chat_messages')
                  .select('id', { count: 'exact', head: false })
                  .eq('group_id', group.id)
                  .eq('sender_type', 'school');

                if (lastReadTimestamp) {
                  query = query.gt('created_at', lastReadTimestamp);
                }

                const { count } = await query;
                counts['admin'] = count || 0;
                break;
              }
            }
          } else {
            counts['admin'] = 0;
          }
          continue;
        }

        // Find chat group for this teacher
        const { data: groups } = await supabase
          .from('chat_groups')
          .select('id')
          .eq('school_id', schoolId)
          .eq('group_type', 'custom')
          .ilike('name', `%${parentData.first_name}%${teacher.first_name}%`);

        if (groups && groups.length > 0) {
          for (const group of groups) {
            const { data: members } = await supabase
              .from('chat_group_members')
              .select('*')
              .eq('group_id', group.id);

            const hasParent = members?.some(m => m.user_type === 'parent' && m.user_id === parentData.id?.toString());
            const hasTeacher = members?.some(m => m.user_type === 'teacher' && m.user_id === teacher.id.toString());

            if (hasParent && hasTeacher) {
              const lastReadKey = `lastRead_${parentData.id}_${teacher.id}`;
              const lastReadTimestamp = localStorage.getItem(lastReadKey);

              let query = supabase
                .from('chat_messages')
                .select('id', { count: 'exact', head: false })
                .eq('group_id', group.id)
                .eq('sender_type', 'teacher')
                .eq('sender_id', teacher.id.toString());

              if (lastReadTimestamp) {
                query = query.gt('created_at', lastReadTimestamp);
              }

              const { count } = await query;
              counts[teacher.id] = count || 0;
              break;
            }
          }
        } else {
          counts[teacher.id] = 0;
        }
      } catch (err) {
        console.error(`Error calculating unread for teacher ${teacher.id}:`, err);
        counts[teacher.id] = 0;
      }
    }

    setUnreadCounts(counts);
  };

  const loadOrCreateChatGroup = async () => {
    if (!selectedTeacher || !parentData) return;

    try {
      setChatLoading(true);
      const schoolId = parentData.school_id || children[0]?.school_id;
      if (!schoolId) {
        alert('School information not found');
        return;
      }

      // Handle admin chat
      if (selectedTeacher.isAdmin) {
        const groupName = `Admin Chat: ${parentData.first_name} ${parentData.last_name}`;
        
        // Check if admin chat group already exists
        let { data: existingGroups } = await supabase
          .from('chat_groups')
          .select('*')
          .eq('school_id', schoolId)
          .eq('group_type', 'custom')
          .ilike('name', `%Admin Chat%${parentData.first_name}%`);

        let group = null;

        if (existingGroups && existingGroups.length > 0) {
          for (const g of existingGroups) {
            const { data: members } = await supabase
              .from('chat_group_members')
              .select('*')
              .eq('group_id', g.id);

            const hasParent = members?.some(m => m.user_type === 'parent' && m.user_id === parentData.id?.toString());
            const hasAdmin = members?.some(m => m.user_type === 'school' && m.user_id === schoolId.toString());

            if (hasParent && hasAdmin) {
              group = g;
              break;
            }
          }
        }

        // Create new admin chat group if doesn't exist
        if (!group) {
          const { data: newGroup, error: createError } = await supabase
            .from('chat_groups')
            .insert({
              school_id: schoolId,
              name: groupName,
              group_type: 'custom',
              created_by: 'parent'
            })
            .select()
            .single();

          if (createError) {
            console.error('Error creating admin chat group:', createError);
            alert('Failed to create chat. Please try again.');
            return;
          }

          group = newGroup;

          // Add parent as member
          await supabase
            .from('chat_group_members')
            .insert({
              group_id: group.id,
              user_type: 'parent',
              user_id: parentData.id.toString(),
              display_name: `${parentData.first_name} ${parentData.last_name}`
            });

          // Add admin (school) as member
          await supabase
            .from('chat_group_members')
            .insert({
              group_id: group.id,
              user_type: 'school',
              user_id: schoolId.toString(),
              display_name: 'School Admin'
            });
        }

        setChatGroup(group);

        // Load messages
        const { data: messages, error: messagesError } = await supabase
          .from('chat_messages')
          .select('*')
          .eq('group_id', group.id)
          .order('created_at', { ascending: true });

        if (messagesError) {
          console.error('Error loading messages:', messagesError);
        } else {
          setChatMessages(messages || []);
          
          // Mark messages as read
          if (messages && messages.length > 0 && selectedTeacher) {
            const lastMessage = messages[messages.length - 1];
            const lastReadKey = `lastRead_${parentData.id}_admin`;
            localStorage.setItem(lastReadKey, lastMessage.created_at);
            
            setUnreadCounts(prev => ({
              ...prev,
              'admin': 0
            }));
          }
        }

        // Clean up existing subscription
        if (window.currentChatChannel) {
          supabase.removeChannel(window.currentChatChannel);
          window.currentChatChannel = null;
        }

        // Set up real-time subscription
        const channel = supabase
          .channel(`chat:${group.id}`)
          .on('postgres_changes', {
            event: 'INSERT',
            schema: 'public',
            table: 'chat_messages',
            filter: `group_id=eq.${group.id}`
          }, (payload) => {
            const newMessage = payload.new;
            
            setChatMessages(prev => {
              const exists = prev.some(m => m.id === newMessage.id);
              if (exists) return prev;
              
              if (newMessage.sender_type === 'school' && selectedTeacher) {
                const lastReadKey = `lastRead_${parentData.id}_admin`;
                const lastReadTimestamp = localStorage.getItem(lastReadKey);
                
                if (!lastReadTimestamp || newMessage.created_at > lastReadTimestamp) {
                  setUnreadCounts(prevCounts => ({
                    ...prevCounts,
                    'admin': (prevCounts['admin'] || 0) + 1
                  }));
                }
              }
              
              return [...prev, newMessage].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            });

            setTimeout(() => {
              if (chatMessagesEndRef.current) {
                chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
              }
            }, 100);
          })
          .subscribe();

        window.currentChatChannel = channel;

        setTimeout(() => {
          if (chatMessagesEndRef.current) {
            chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
        
        setChatLoading(false);
        return;
      }

      // Regular teacher chat (existing code)
      const groupName = `Chat: ${parentData.first_name} ${parentData.last_name} - ${selectedTeacher.first_name} ${selectedTeacher.last_name}`;

      // Check if chat group already exists
      let { data: existingGroups } = await supabase
        .from('chat_groups')
        .select('*')
        .eq('school_id', schoolId)
        .eq('group_type', 'custom')
        .ilike('name', `%${parentData.first_name}%${selectedTeacher.first_name}%`);

      let group = null;

      if (existingGroups && existingGroups.length > 0) {
        for (const g of existingGroups) {
          const { data: members } = await supabase
            .from('chat_group_members')
            .select('*')
            .eq('group_id', g.id);

          const hasParent = members?.some(m => m.user_type === 'parent' && m.user_id === parentData.id?.toString());
          const hasTeacher = members?.some(m => m.user_type === 'teacher' && m.user_id === selectedTeacher.id.toString());

          if (hasParent && hasTeacher) {
            group = g;
            break;
          }
        }
      }

      // Create new group if doesn't exist
      if (!group) {
        const { data: newGroup, error: createError } = await supabase
          .from('chat_groups')
          .insert({
            school_id: schoolId,
            name: groupName,
            group_type: 'custom',
            created_by: 'parent'
          })
          .select()
          .single();

        if (createError) {
          console.error('Error creating chat group:', createError);
          alert('Failed to create chat. Please try again.');
          return;
        }

        group = newGroup;

        // Add parent as member
        await supabase
          .from('chat_group_members')
          .insert({
            group_id: group.id,
            user_type: 'parent',
            user_id: parentData.id?.toString() || parentData.email,
            display_name: `${parentData.first_name} ${parentData.last_name}`
          });

        // Add teacher as member
        await supabase
          .from('chat_group_members')
          .insert({
            group_id: group.id,
            user_type: 'teacher',
            user_id: selectedTeacher.id.toString(),
            display_name: `${selectedTeacher.first_name} ${selectedTeacher.last_name}`
          });
      }

      setChatGroup(group);

      // Load messages
      const { data: messages, error: messagesError } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('group_id', group.id)
        .order('created_at', { ascending: true });

      if (messagesError) {
        console.error('Error loading messages:', messagesError);
      } else {
        setChatMessages(messages || []);
        
        if (messages && messages.length > 0 && selectedTeacher) {
          const lastMessage = messages[messages.length - 1];
          const lastReadKey = `lastRead_${parentData.id}_${selectedTeacher.id}`;
          localStorage.setItem(lastReadKey, lastMessage.created_at);
          
          setUnreadCounts(prev => ({
            ...prev,
            [selectedTeacher.id]: 0
          }));
        }
      }

      // Set up real-time subscription
      if (window.currentChatChannel) {
        supabase.removeChannel(window.currentChatChannel);
        window.currentChatChannel = null;
      }

      const channel = supabase
        .channel(`chat:${group.id}`, {
          config: {
            broadcast: { self: false }
          }
        })
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `group_id=eq.${group.id}`
        }, (payload) => {
          const newMessage = payload.new;
          setChatMessages(prev => {
            const exists = prev.some(m => m.id === newMessage.id);
            if (exists) return prev;
            
            if (newMessage.sender_type === 'teacher' && selectedTeacher) {
              const lastReadKey = `lastRead_${parentData.id}_${selectedTeacher.id}`;
              const lastReadTimestamp = localStorage.getItem(lastReadKey);
              
              if (!lastReadTimestamp || newMessage.created_at > lastReadTimestamp) {
                setUnreadCounts(prevCounts => ({
                  ...prevCounts,
                  [selectedTeacher.id]: (prevCounts[selectedTeacher.id] || 0) + 1
                }));
              }
            }
            
            return [...prev, newMessage];
          });
        })
        .subscribe();

      window.currentChatChannel = channel;
    } catch (err) {
      console.error('Error loading chat:', err);
      alert('Failed to load chat. Please try again.');
    } finally {
      setChatLoading(false);
    }
  };

  // Upload files to Supabase storage
  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return [];
    
    const uploadedUrls = [];
    setUploadingFiles(true);
    
    try {
      const bucketName = 'chat-uploads';
      const schoolId = parentData.school_id || children[0]?.school_id;
      
      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `chat_${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${schoolId}/${fileName}`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from(bucketName)
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });
        
        if (uploadError) {
          console.error('Error uploading file:', uploadError);
          if (uploadError.message.includes('Bucket not found')) {
            alert('Storage bucket not found. Please create "chat-uploads" bucket in Supabase Storage.');
            continue;
          }
          continue;
        }
        
        const { data: urlData } = supabase.storage
          .from(bucketName)
          .getPublicUrl(filePath);
        
        uploadedUrls.push({
          url: urlData.publicUrl,
          name: file.name,
          type: file.type,
          size: file.size
        });
      }
    } catch (err) {
      console.error('Error uploading files:', err);
    } finally {
      setUploadingFiles(false);
    }
    
    return uploadedUrls;
  };

  const sendMessage = async () => {
    if ((!newMessage.trim() && selectedFiles.length === 0) || !chatGroup || !parentData) return;

    try {
      const schoolId = parentData.school_id || children[0]?.school_id;
      
      let mediaUrls = [];
      if (selectedFiles.length > 0) {
        mediaUrls = await uploadFiles(selectedFiles);
        setSelectedFiles([]);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }

      const { data, error } = await supabase
        .from('chat_messages')
        .insert({
          school_id: schoolId,
          group_id: chatGroup.id,
          sender_type: 'parent',
          sender_id: parentData.id?.toString() || parentData.email,
          content: newMessage.trim() || null,
          media_urls: mediaUrls.length > 0 ? mediaUrls : null
        })
        .select()
        .single();

      if (error) {
        console.error('Error sending message:', error);
        alert('Failed to send message. Please try again.');
        return;
      }

      setChatMessages(prev => {
        const exists = prev.some(m => m.id === data.id);
        if (exists) return prev;
        return [...prev, data];
      });

      setNewMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message. Please try again.');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      if (file.size > 10 * 1024 * 1024) {
        alert(`${file.name} is too large. Maximum file size is 10MB.`);
        return false;
      }
      return true;
    }).slice(0, 5);
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  // Mark message as read
  const markMessageAsRead = async (messageId, message) => {
    if (!message || !parentData) return;
    
    // Only mark messages sent by others as read
    if (message.sender_type === 'parent' && message.sender_id === (parentData.id?.toString() || parentData.email)) {
      return; // Don't mark own messages
    }

    try {
      const readBy = message.read_by || [];
      const readerId = parentData.id?.toString() || parentData.email;
      const readerType = 'parent';
      
      // Check if already marked as read by this user
      const alreadyRead = readBy.some(
        reader => reader.user_id === readerId && reader.user_type === readerType
      );
      
      if (alreadyRead) return;

      // Add current user to read_by array
      const updatedReadBy = [
        ...readBy,
        {
          user_id: readerId,
          user_type: readerType,
          read_at: new Date().toISOString()
        }
      ];

      await supabase
        .from('chat_messages')
        .update({ read_by: updatedReadBy })
        .eq('id', messageId);

      // Update local state
      setChatMessages(prev =>
        prev.map(msg =>
          msg.id === messageId
            ? { ...msg, read_by: updatedReadBy }
            : msg
        )
      );
    } catch (err) {
      console.error('Error marking message as read:', err);
    }
  };

  // Get message status indicator
  const getMessageStatus = (message) => {
    if (!message || message.sender_type !== 'parent' || message.sender_id !== (parentData.id?.toString() || parentData.email)) {
      return null; // Only show status for own messages
    }

    const readBy = message.read_by || [];
    
    // Check if recipient has read the message
    if (selectedTeacher) {
      const recipientId = selectedTeacher.isAdmin 
        ? (parentData.school_id || children[0]?.school_id)?.toString()
        : selectedTeacher.id.toString();
      const recipientType = selectedTeacher.isAdmin ? 'school' : 'teacher';
      
      const isRead = readBy.some(
        reader => reader.user_id === recipientId && reader.user_type === recipientType
      );
      
      if (isRead) {
        return { status: 'read', icon: '✓✓', color: '#4F46E5' }; // Blue double check
      }
    }
    
    // Message is delivered (exists in DB) but not read
    return { status: 'delivered', icon: '✓✓', color: '#9CA3AF' }; // Gray double check
  };

  // AI Assistant Functions
  const sendAiMessage = async () => {
    if (!aiChatInput.trim() || aiChatLoading) return;

    const userMessage = aiChatInput.trim();
    const timestamp = new Date().toISOString();

    // Add user message to chat
    const userMsg = {
      id: Date.now(),
      role: 'user',
      content: userMessage,
      timestamp
    };
    setAiChatMessages(prev => [...prev, userMsg]);
    setAiChatInput('');
    setAiChatLoading(true);

    try {
      // Try Google Gemini API first
      let aiResponse = null;
      try {
        aiResponse = await tryGoogleGemini(userMessage);
      } catch (geminiError) {
        console.log('Gemini API not available, using smart assistant:', geminiError);
        // Fall through to use smart assistant
      }
      
      // If Gemini fails, use smart rule-based assistant
      if (!aiResponse) {
        aiResponse = getSmartAssistantResponse(userMessage);
      }

      // Add AI response to chat
      const aiMsg = {
        id: Date.now() + 1,
        role: 'assistant',
        content: aiResponse,
        timestamp: new Date().toISOString()
      };
      setAiChatMessages(prev => [...prev, aiMsg]);
    } catch (error) {
      console.error('AI Chat Error:', error);
      
      // Fallback to smart assistant
      const fallbackResponse = getSmartAssistantResponse(userMessage);
      const errorMsg = {
        id: Date.now() + 1,
        role: 'assistant',
        content: fallbackResponse,
        timestamp: new Date().toISOString()
      };
      setAiChatMessages(prev => [...prev, errorMsg]);
    } finally {
      setAiChatLoading(false);
    }
  };

  // List available Gemini models
  const listAvailableModels = async (apiKey) => {
    try {
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models?key=${apiKey}`
      );
      if (response.ok) {
        const data = await response.json();
        return data.models?.map(m => m.name) || [];
      }
    } catch (err) {
      console.error('Error listing models:', err);
    }
    return [];
  };

  // Use Google Gemini API
  const tryGoogleGemini = async (question) => {
    // Get API key from localStorage (default key is set automatically)
    const geminiApiKey = localStorage.getItem('gemini_api_key') || 'AIzaSyBIRBgSh5JpYKU2-9sW4-XAeZNyRK5HzCg';
    
    // First, try to get available models
    const availableModels = await listAvailableModels(geminiApiKey);
    console.log('Available Gemini models:', availableModels);
    
    // Try different API endpoints and models
    // Prioritize newer models and available models from API
    // Filter out embedding models (they don't support generateContent)
    const textGenerationModels = availableModels.length > 0 
      ? availableModels
          .filter(modelName => 
            !modelName.includes('embedding') && 
            !modelName.includes('imagen') && 
            !modelName.includes('veo') &&
            !modelName.includes('aqa')
          )
          .map(modelName => ({
            version: 'v1beta',
            model: modelName.replace('models/', '')
          }))
      : [];
    
    const apiConfigs = [
      // First, try available models from API (most reliable)
      ...textGenerationModels,
      // Then try latest stable models (v2.5)
      { version: 'v1beta', model: 'gemini-2.5-flash' },
      { version: 'v1beta', model: 'gemini-2.5-pro' },
      { version: 'v1beta', model: 'gemini-flash-latest' },
      { version: 'v1beta', model: 'gemini-pro-latest' },
      // Try v2.0 models as fallback
      { version: 'v1beta', model: 'gemini-2.0-flash' },
      { version: 'v1beta', model: 'gemini-2.0-pro-exp' },
      // Legacy models (last resort)
      { version: 'v1beta', model: 'gemini-1.5-flash' },
      { version: 'v1beta', model: 'gemini-1.5-pro' },
    ];
    
    for (const config of apiConfigs) {
      try {
        const url = `https://generativelanguage.googleapis.com/${config.version}/models/${config.model}:generateContent?key=${geminiApiKey}`;
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            contents: [{
              parts: [{
                text: `You are a helpful and friendly AI assistant for parents. Help them with questions about their children's education, school activities, homework help, and parenting tips. Be encouraging and provide clear, practical advice.

Parent Question: ${question}

Provide a helpful, educational response:`
              }]
            }],
            generationConfig: {
              temperature: 0.7,
              topK: 40,
              topP: 0.95,
              maxOutputTokens: 1024,
            }
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error(`Gemini API HTTP error (${config.version}/${config.model}):`, response.status, errorData);
          
          // Log detailed error for debugging
          if (errorData.error) {
            console.error('Detailed error:', {
              code: errorData.error.code,
              message: errorData.error.message,
              status: errorData.error.status
            });
          }
          
          // If it's a model not found error (404), try next config
          if (response.status === 404) {
            continue; // Try next config
          }
          
          // If it's authentication error (401/403), don't try other configs
          if (response.status === 401 || response.status === 403) {
            throw new Error(errorData.error?.message || `Authentication error: ${response.status}. Please check your API key.`);
          }
          
          // If it's the last config, return null to use fallback
          if (config === apiConfigs[apiConfigs.length - 1]) {
            return null; // Use fallback instead of throwing
          }
          continue;
        }

        const data = await response.json();
        console.log('Gemini API response:', data);
        
        if (data.candidates && data.candidates[0]?.content?.parts?.[0]?.text) {
          return data.candidates[0].content.parts[0].text.trim();
        } else if (data.error) {
          console.error('Gemini API error in response:', data.error);
          // Try next config if available
          if (config !== apiConfigs[apiConfigs.length - 1]) {
            continue;
          }
          throw new Error(data.error.message || 'Failed to get response');
        } else {
          console.error('Unexpected response format:', data);
          // Try next config if available
          if (config !== apiConfigs[apiConfigs.length - 1]) {
            continue;
          }
          throw new Error('Unexpected response format from API');
        }
      } catch (err) {
        console.error(`Gemini API error (${config.version}/${config.model}):`, err);
        // If it's the last config, throw the error
        if (config === apiConfigs[apiConfigs.length - 1]) {
          throw err;
        }
        // Otherwise, continue to next config
        continue;
      }
    }
    
    // If we get here, all configs failed - return null to use fallback
    return null;
  };

  // Smart Rule-Based Assistant - Works without API
  const getSmartAssistantResponse = (question) => {
    const lowerQuestion = question.toLowerCase().trim();
    
    // Math calculations
    if (lowerQuestion.match(/\d+\s*[+\-*/×÷]\s*\d+/)) {
      try {
        const mathMatch = question.match(/(\d+)\s*([+\-*/×÷])\s*(\d+)/);
        if (mathMatch) {
          const num1 = parseFloat(mathMatch[1]);
          const op = mathMatch[2] === '×' ? '*' : mathMatch[2] === '÷' ? '/' : mathMatch[2];
          const num2 = parseFloat(mathMatch[3]);
          let result;
          switch(op) {
            case '+': result = num1 + num2; break;
            case '-': result = num1 - num2; break;
            case '*': result = num1 * num2; break;
            case '/': result = num2 !== 0 ? (num1 / num2).toFixed(2) : 'undefined (division by zero)'; break;
            default: break;
          }
          if (result !== undefined) {
            return `The answer is: **${result}**\n\n${num1} ${op} ${num2} = ${result}`;
          }
        }
      } catch (e) {}
    }

    // Homework help for parents
    if (lowerQuestion.includes('homework') || lowerQuestion.includes('assignment') || lowerQuestion.includes('help with')) {
      return `Helping Your Child with Homework:\n\n1. **Create a Routine**: Set a regular time and place for homework\n2. **Be Available**: Offer help but don't do the work for them\n3. **Break It Down**: Help them break large assignments into smaller tasks\n4. **Encourage Independence**: Let them try first, then offer guidance\n5. **Stay Positive**: Praise effort, not just results\n6. **Communicate with Teachers**: Reach out if your child is struggling\n7. **Limit Distractions**: Ensure a quiet, organized workspace\n8. **Take Breaks**: Encourage short breaks to maintain focus\n\n**For specific subjects:**\n- Math: Help them understand concepts, check their work\n- Reading: Read together, discuss what they're reading\n- Science: Do experiments together, explore real-world applications\n- Writing: Help them organize ideas, review for clarity\n\nWhat subject does your child need help with?`;
    }

    // School communication
    if (lowerQuestion.includes('school') && (lowerQuestion.includes('communicate') || lowerQuestion.includes('talk') || lowerQuestion.includes('contact'))) {
      return `Communicating with Your Child's School:\n\n1. **Use the Chat Feature**: This dashboard has a chat feature to message teachers directly\n2. **Attend Parent-Teacher Meetings**: Regular communication is key\n3. **Be Proactive**: Don't wait for problems - check in regularly\n4. **Be Respectful**: Approach conversations with respect and understanding\n5. **Ask Specific Questions**: Be clear about what you need to know\n6. **Follow Up**: If you don't hear back, follow up politely\n7. **Use Email or School Portal**: Many schools have online portals for communication\n\nYou can use the Chat tab in this dashboard to message your child's teachers directly!`;
    }

    // Academic performance
    if (lowerQuestion.includes('grades') || lowerQuestion.includes('performance') || lowerQuestion.includes('results') || lowerQuestion.includes('marks')) {
      return `Supporting Your Child's Academic Performance:\n\n1. **Review Results Together**: Look at report cards and test results together\n2. **Focus on Effort**: Praise hard work, not just grades\n3. **Identify Strengths**: Celebrate what they're good at\n4. **Address Weaknesses**: Work together on areas that need improvement\n5. **Set Realistic Goals**: Help them set achievable academic goals\n6. **Provide Resources**: Ensure they have books, materials, and quiet study space\n7. **Encourage Reading**: Reading improves performance across all subjects\n8. **Stay Involved**: Regularly check homework and school communications\n\nYou can view your child's results in the "Results" tab of this dashboard!`;
    }

    // Attendance
    if (lowerQuestion.includes('attendance') || lowerQuestion.includes('absent') || lowerQuestion.includes('present')) {
      return `Understanding Your Child's Attendance:\n\n1. **Check Regularly**: Monitor attendance in the Attendance tab\n2. **Understand Patterns**: Look for patterns in absences\n3. **Communicate**: Inform school if your child will be absent\n4. **Follow School Policy**: Understand the school's attendance requirements\n5. **Address Issues**: If there are concerns, discuss with teachers\n\nYou can view your child's attendance records in the "Attendance" tab!`;
    }

    // Fees and payments
    if (lowerQuestion.includes('fee') || lowerQuestion.includes('payment') || lowerQuestion.includes('pay')) {
      return `Managing School Fees:\n\n1. **Check Fees Tab**: View all fee information in the Fees tab\n2. **Payment Deadlines**: Be aware of due dates\n3. **Payment Methods**: Check what payment methods the school accepts\n4. **Payment History**: Keep track of what you've paid\n5. **Contact School**: If you have questions about fees, contact the school office\n\nYou can view all fee information in the "Fees" tab of this dashboard!`;
    }

    // General parenting tips
    if (lowerQuestion.includes('parent') && (lowerQuestion.includes('tip') || lowerQuestion.includes('advice') || lowerQuestion.includes('help'))) {
      return `Parenting Tips for School Success:\n\n1. **Stay Involved**: Know what's happening at school\n2. **Establish Routines**: Consistent routines help children succeed\n3. **Encourage Reading**: Read together and make books available\n4. **Limit Screen Time**: Balance technology with other activities\n5. **Communicate**: Talk to your child about their day\n6. **Support Learning**: Help with homework but encourage independence\n7. **Celebrate Success**: Acknowledge achievements, big and small\n8. **Be Patient**: Learning takes time and effort\n9. **Stay Positive**: Maintain a positive attitude about school\n10. **Work with Teachers**: Build a partnership with educators\n\nWhat specific area would you like help with?`;
    }

    // General greeting
    if (lowerQuestion.match(/^(hi|hello|hey|good morning|good afternoon|good evening)/)) {
      return `Hello! 👋 I'm your AI Assistant for Parents!\n\nI can help you with:\n- 📚 Understanding your child's education\n- 📝 Homework help strategies\n- 💬 School communication tips\n- 📊 Academic performance guidance\n- 📅 Attendance and schedule information\n- 💰 Fee and payment questions\n- 👨‍👩‍👧 Parenting tips for school success\n\nWhat would you like help with today?`;
    }

    // General educational response
    return `I'm here to help you support your child's education! 📚\n\nI can assist with:\n- Understanding school activities and schedules\n- Homework help strategies\n- Communicating with teachers\n- Academic performance guidance\n- Attendance and fees information\n- General parenting tips\n\nCould you be more specific about what you need help with? For example:\n- "How can I help my child with homework?"\n- "How do I communicate with teachers?"\n- "What should I know about my child's attendance?"\n\nYou can also use the various tabs in this dashboard to view your child's information directly!`;
  };


  // Save API key
  const handleSaveApiKey = () => {
    if (apiKeyInput.trim() && !apiKeyInput.includes('...')) {
      localStorage.setItem('gemini_api_key', apiKeyInput.trim());
      setHasApiKey(true);
      setShowApiKeySettings(false);
      setApiKeyInput('');
      alert('✅ API key saved successfully! You\'ll now get better AI responses.');
    }
  };

  // Fetch transport data for all children
  const fetchTransportDataForChildren = async () => {
    if (children.length === 0) return;
    
    setTransportLoading(true);
    try {
      const transportDataMap = {};
      
      // Fetch transport data for each child
      for (const child of children) {
        // Check if child has transport enabled
        if (!child.transport_service_enabled) {
          transportDataMap[child.id] = null;
          continue;
        }

        // Fetch route information
        let routeData = null;
        if (child.route_id) {
          const { data: route, error: routeError } = await supabase
            .from('routes')
            .select(`
              *,
              transport_vehicles:vehicle_id (
                id,
                bus_number,
                driver_name,
                capacity
              )
            `)
            .eq('id', child.route_id)
            .single();

          if (!routeError && route) {
            routeData = {
              ...route,
              vehicle: route.transport_vehicles || null,
              seatNumber: child.bus_seat_number || null
            };

            // Fetch pickup location
            if (child.pickup_location_id) {
              const { data: pickupLocation, error: locationError } = await supabase
                .from('pickup_locations')
                .select('*')
                .eq('id', child.pickup_location_id)
                .single();

              if (!locationError && pickupLocation) {
                routeData.pickupLocation = pickupLocation;
              }
            }

            // Fetch all pickup locations for this route
            const { data: routeLocations, error: routeLocError } = await supabase
              .from('route_pickup_locations')
              .select('sequence_order, pickup_location_id')
              .eq('route_id', route.id)
              .order('sequence_order', { ascending: true });

            if (!routeLocError && routeLocations) {
              const locationIds = routeLocations.map(rl => rl.pickup_location_id);
              const { data: locations, error: locsError } = await supabase
                .from('pickup_locations')
                .select('id, location_name, address, latitude, longitude')
                .in('id', locationIds);

              if (!locsError && locations) {
                // Geocode addresses that don't have coordinates
                const geocodeAddress = async (address, locationName) => {
                  try {
                    const searchQuery = locationName && address 
                      ? `${locationName}, ${address}, Rawalpindi, Pakistan`
                      : address 
                      ? `${address}, Rawalpindi, Pakistan`
                      : locationName
                      ? `${locationName}, Rawalpindi, Pakistan`
                      : null;

                    if (!searchQuery) return null;

                    const response = await fetch(
                      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`,
                      {
                        headers: {
                          'User-Agent': 'ParentPortal/1.0'
                        }
                      }
                    );
                    
                    const data = await response.json();
                    
                    if (data && data.length > 0) {
                      return {
                        latitude: parseFloat(data[0].lat),
                        longitude: parseFloat(data[0].lon)
                      };
                    }
                    return null;
                  } catch (error) {
                    console.error('Geocoding error:', error);
                    return null;
                  }
                };

                // Process locations and geocode if needed
                routeData.allPickupLocations = await Promise.all(
                  routeLocations.map(async (routeLoc) => {
                    const location = locations.find(loc => loc.id === routeLoc.pickup_location_id);
                    if (!location) return null;

                    let finalLocation = {
                      ...location,
                      sequence_order: routeLoc.sequence_order
                    };

                    // If location doesn't have coordinates, try to geocode
                    if (!location.latitude || !location.longitude) {
                      const coords = await geocodeAddress(location.address, location.location_name);
                      if (coords) {
                        finalLocation.latitude = coords.latitude.toString();
                        finalLocation.longitude = coords.longitude.toString();
                      }
                    }

                    return finalLocation;
                  })
                );
                routeData.allPickupLocations = routeData.allPickupLocations.filter(Boolean);
              }
            }
          }
        }

        transportDataMap[child.id] = routeData;
      }

      setChildrenTransportData(transportDataMap);
      
      // Try to get bus locations
      await updateBusLocations();
    } catch (error) {
      console.error('Error fetching transport data:', error);
    } finally {
      setTransportLoading(false);
    }
  };

  // Update bus locations for all children
  const updateBusLocations = async () => {
    const busLocationMap = {};
    
    for (const child of children) {
      const transportData = childrenTransportData[child.id];
      if (!transportData || !transportData.id) {
        continue;
      }

      try {
        const routeId = transportData.id;
        const activeRouteKey = `activeRoute_${routeId}`;
        const activeRouteData = localStorage.getItem(activeRouteKey);
        
        if (activeRouteData) {
          try {
            const routeInfo = JSON.parse(activeRouteData);
            const locationTimestamp = new Date(routeInfo.timestamp);
            const now = new Date();
            const timeDiff = (now - locationTimestamp) / 1000 / 60;
            
            if (timeDiff < 10 && routeInfo.latitude && routeInfo.longitude) {
              busLocationMap[child.id] = {
                lat: parseFloat(routeInfo.latitude),
                lng: parseFloat(routeInfo.longitude),
                timestamp: routeInfo.timestamp
              };
              continue;
            }
          } catch (parseError) {
            console.error('Error parsing active route data:', parseError);
          }
        }
        
        // Check all localStorage keys for matching driver
        if (transportData.vehicle && transportData.vehicle.driver_name) {
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('activeRoute_')) {
              try {
                const routeData = JSON.parse(localStorage.getItem(key));
                if (routeData.driverName === transportData.vehicle.driver_name) {
                  const locationTimestamp = new Date(routeData.timestamp);
                  const now = new Date();
                  const timeDiff = (now - locationTimestamp) / 1000 / 60;
                  
                  if (timeDiff < 10 && routeData.latitude && routeData.longitude) {
                    busLocationMap[child.id] = {
                      lat: parseFloat(routeData.latitude),
                      lng: parseFloat(routeData.longitude),
                      timestamp: routeData.timestamp
                    };
                    break;
                  }
                }
              } catch (e) {
                continue;
              }
            }
          }
        }
      } catch (error) {
        console.error(`Error updating bus location for child ${child.id}:`, error);
      }
    }
    
    setBusLocations(busLocationMap);
  };

  // Remove API key
  const handleRemoveApiKey = () => {
    if (window.confirm('Are you sure you want to remove your API key?')) {
      localStorage.removeItem('gemini_api_key');
      setHasApiKey(false);
      setApiKeyInput('');
      setShowApiKeySettings(false);
      alert('API key removed. The assistant will still work with offline responses.');
    }
  };

  // Open API key settings
  const handleOpenApiKeySettings = () => {
    const storedKey = localStorage.getItem('gemini_api_key');
    if (storedKey) {
      setApiKeyInput(storedKey.substring(0, 10) + '...' + storedKey.substring(storedKey.length - 4));
    } else {
      setApiKeyInput('');
    }
    setShowApiKeySettings(true);
  };

  // Fetch meals for all children's schools
  const fetchMealsForChildren = async () => {
    if (children.length === 0) return;
    
    try {
      setMealsLoading(true);
      
      // Get unique school IDs from children
      const schoolIds = [...new Set(children.map(child => child.school_id).filter(Boolean))];
      
      if (schoolIds.length === 0) {
        setMeals([]);
        return;
      }

      // Fetch meals for all schools
      const { data, error } = await supabase
        .from('meals')
        .select('*')
        .in('school_id', schoolIds)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching meals:', error);
        setMeals([]);
      } else {
        setMeals(data || []);
      }
    } catch (err) {
      console.error('Error fetching meals:', err);
      setMeals([]);
    } finally {
      setMealsLoading(false);
    }
  };

  // Scroll to bottom when chat messages change
  useEffect(() => {
    if (chatMessagesEndRef.current && selectedTeacher && parentData) {
      chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      
      if (chatMessages && chatMessages.length > 0) {
        const lastMessage = chatMessages[chatMessages.length - 1];
        const lastReadKey = `lastRead_${parentData.id}_${selectedTeacher.id}`;
        localStorage.setItem(lastReadKey, lastMessage.created_at);
        
        setUnreadCounts(prev => ({
          ...prev,
          [selectedTeacher.id]: 0
        }));
      }
    }
  }, [chatMessages, selectedTeacher, parentData]);

  useEffect(() => {
    if (activeTab === 'chat' && children.length > 0 && parentData) {
      fetchTeachersForChildren();
    } else if (activeTab !== 'chat') {
      setSelectedTeacher(null);
      setChatMessages([]);
      setChatGroup(null);
      setShowSidebar(true);
      if (window.currentChatChannel) {
        supabase.removeChannel(window.currentChatChannel);
        window.currentChatChannel = null;
      }
    }
  }, [activeTab, children, parentData]);

  useEffect(() => {
    if (activeTab === 'chat' && selectedTeacher && parentData && !chatGroup) {
      loadOrCreateChatGroup();
    } else if (activeTab === 'chat' && !selectedTeacher) {
      setChatMessages([]);
      setChatGroup(null);
      if (window.currentChatChannel) {
        supabase.removeChannel(window.currentChatChannel);
        window.currentChatChannel = null;
      }
    }
  }, [selectedTeacher, activeTab, parentData]);

  const formatNoticeDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      high: 'High Priority',
      medium: 'Medium Priority',
      normal: 'Normal'
    };
    return labels[priority] || 'Normal';
  };

  const getPriorityIcon = (priority) => {
    const icons = {
      high: '🔴',
      medium: '🟡',
      normal: '🔵'
    };
    return icons[priority] || '🔵';
  };

  const getPriorityClass = (priority) => {
    return `priority-${priority || 'normal'}`;
  };

  if (loading) {
    return (
      <div className="parent-dashboard">
        <div className="loading-container">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="parent-dashboard">
      <header className="dashboard-header">
        <div className="header-left">
          <div className="student-info">
            <div className="school-name-header" style={{ color: '#FF8B3D', fontWeight: '900', fontSize: '20px' }}>
              {children.length > 0 && children[0]?.schools?.school_name ? children[0].schools.school_name : 'School Name'}
            </div>
          </div>
        </div>
        <div className="header-right">
          <div className="user-profile desktop-only">
            <div className="user-avatar">
              {parentData?.first_name?.charAt(0).toUpperCase()}{parentData?.last_name?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details-header">
              <div className="user-name-header">{parentData?.first_name} {parentData?.last_name}</div>
              <div className="user-role-header">Parent</div>
            </div>
          </div>
          <button className="logout-btn-header desktop-only" onClick={handleLogout}>
            Logout
          </button>
          <button 
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            style={{ color: '#FF8B3D', fontWeight: '900' }}
          >
            {mobileMenuOpen ? '✕' : '☰'}
          </button>
        </div>
      </header>

      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}>
          <div className="mobile-menu-dropdown" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <div className="mobile-user-info">
                <div className="user-avatar">
                  {parentData?.first_name?.charAt(0).toUpperCase()}{parentData?.last_name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="mobile-user-name">{parentData?.first_name} {parentData?.last_name}</div>
                  <div className="mobile-user-role">Parent</div>
                </div>
              </div>
              <button className="mobile-logout-btn" onClick={handleLogout}>
                Logout
              </button>
            </div>
            <div className="mobile-menu-tabs">
              <button 
                className={`mobile-menu-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('dashboard');
                  setMobileMenuOpen(false);
                }}
              >
                Dashboard
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'children' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('children');
                  setMobileMenuOpen(false);
                }}
              >
                Children
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'homework' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('homework');
                  setMobileMenuOpen(false);
                }}
              >
                Homework
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'attendance' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('attendance');
                  setMobileMenuOpen(false);
                }}
              >
                Attendance
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'fees' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('fees');
                  setMobileMenuOpen(false);
                }}
              >
                Fees
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'schedule' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('schedule');
                  setMobileMenuOpen(false);
                }}
              >
                Schedule
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'exam-schedule' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('exam-schedule');
                  setMobileMenuOpen(false);
                }}
              >
                Tests & Exams
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'results' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('results');
                  setMobileMenuOpen(false);
                }}
              >
                Results
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'chat' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('chat');
                  setMobileMenuOpen(false);
                }}
              >
                Chat
              </button>
              <button 
                className={`mobile-menu-tab ${activeTab === 'transport' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('transport');
                  setMobileMenuOpen(false);
                }}
              >
                Transport
              </button>
              <button
                className={`mobile-menu-tab ${activeTab === 'meals' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('meals');
                  setMobileMenuOpen(false);
                }}
              >
                Meals
              </button>
              <button
                className={`mobile-menu-tab ${activeTab === 'policies' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('policies');
                  setMobileMenuOpen(false);
                }}
              >
                Policies
              </button>
              <button
                className={`mobile-menu-tab ${activeTab === 'contact-us' ? 'active' : ''}`}
                onClick={() => {
                  handleTabChange('contact-us');
                  setMobileMenuOpen(false);
                }}
              >
                Contact Us
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="nav-tabs">
        <button
          className={`nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => handleTabChange('dashboard')}
        >
          <span className="tab-text">Dashboard</span>
        </button>
        <button
          className={`nav-tab ${activeTab === 'children' ? 'active' : ''}`}
          onClick={() => handleTabChange('children')}
        >
          <span className="tab-text">Children</span>
        </button>
        <button
          className={`nav-tab ${activeTab === 'homework' ? 'active' : ''}`}
          onClick={() => handleTabChange('homework')}
        >
          <span className="tab-text">Homework</span>
        </button>
        <button
          className={`nav-tab ${activeTab === 'attendance' ? 'active' : ''}`}
          onClick={() => handleTabChange('attendance')}
        >
          <span className="tab-text">Attendance</span>
        </button>
        <button
          className={`nav-tab ${activeTab === 'fees' ? 'active' : ''}`}
          onClick={() => handleTabChange('fees')}
        >
          <span className="tab-text">Fees</span>
        </button>
        <button
          className={`nav-tab ${activeTab === 'schedule' ? 'active' : ''}`}
          onClick={() => handleTabChange('schedule')}
        >
          <span className="tab-text">Schedule</span>
        </button>
        <button
          className={`nav-tab ${activeTab === 'exam-schedule' ? 'active' : ''}`}
          onClick={() => handleTabChange('exam-schedule')}
        >
          <span className="tab-text">Tests & Exams</span>
        </button>
        <button
          className={`nav-tab ${activeTab === 'results' ? 'active' : ''}`}
          onClick={() => handleTabChange('results')}
        >
          <span className="tab-text">Results</span>
        </button>
        <button
          className={`nav-tab ${activeTab === 'chat' ? 'active' : ''}`}
          onClick={() => handleTabChange('chat')}
        >
          <span className="tab-text">Chat</span>
        </button>
        <button
          className={`nav-tab ${activeTab === 'transport' ? 'active' : ''}`}
          onClick={() => handleTabChange('transport')}
        >
          <span className="tab-text">Transport</span>
        </button>
        <button
          className={`nav-tab ${activeTab === 'meals' ? 'active' : ''}`}
          onClick={() => handleTabChange('meals')}
        >
          <span className="tab-text">Meals</span>
        </button>
      </div>

      <main className="dashboard-main">
        {activeTab === 'dashboard' && (
          <div className="dashboard-content">
            {/* Welcome Header Card */}
            <div className="welcome-card">
              <div className="welcome-card-orange-bar"></div>
              <div className="welcome-card-content">
                <span className="welcome-emoji">👋</span>
                <h2 className="welcome-title">Welcome, {parentData?.first_name || parentDetails?.parent_name || 'Parent'}!</h2>
              <p className="welcome-message">
                Manage your children's academic information and track their progress.
              </p>
              </div>
            </div>

            {/* Your Details Section */}
            <div className="parent-details-card">
              <div className="section-orange-bar"></div>
              <div className="section-header">
                <h3 className="section-title">Your Details</h3>
              </div>
              <div className="details-grid">
                <div className="detail-field">
                  <label className="detail-label">👤 FULL NAME</label>
                  <div className="detail-input">
                    {parentDetails?.parent_name || `${parentData?.first_name || ''} ${parentData?.last_name || ''}`.trim() || 'N/A'}
                </div>
                </div>
                <div className="detail-field">
                  <label className="detail-label">✉️ EMAIL</label>
                  <div className="detail-input">
                    {parentDetails?.parent_email || parentData?.email || 'N/A'}
                </div>
                </div>
                <div className="detail-field">
                  <label className="detail-label">📞 PHONE</label>
                  <div className="detail-input">
                    {parentDetails?.parent_phone || 'N/A'}
                </div>
                </div>
                <div className="detail-field">
                  <label className="detail-label">❤️ RELATIONSHIP</label>
                  <div className="detail-input">
                    {parentDetails?.parent_relationship || 'N/A'}
                </div>
                </div>
                <div className="detail-field full-width">
                  <label className="detail-label">🏠 ADDRESS</label>
                  <div className="detail-input">
                    {parentDetails?.parent_address || 'N/A'}
                  </div>
                </div>
                </div>
              </div>

            {/* Status & Permissions Section */}
            <div className="parent-status-card">
              <div className="section-orange-bar"></div>
              <div className="section-header">
                <h3 className="section-title">Status & Permissions</h3>
                  </div>
              <div className="permissions-grid">
                <div className={`permission-badge ${parentDetails?.parent_is_primary ? 'active' : 'inactive'}`}>
                  <span className="permission-checkmark">✓</span>
                  <span className="permission-text">PRIMARY PARENT</span>
                  </div>
                <div className={`permission-badge ${parentDetails?.parent_can_pickup ? 'active' : 'inactive'}`}>
                  <span className="permission-checkmark">✓</span>
                  <span className="permission-text">CAN PICKUP</span>
                  </div>
                <div className={`permission-badge ${parentDetails?.parent_is_emergency_contact ? 'active' : 'inactive'}`}>
                  <span className="permission-checkmark">✓</span>
                  <span className="permission-text">EMERGENCY CONTACT</span>
                </div>
                <div className={`permission-badge ${parentDetails?.parent_pays_fees ? 'active' : 'inactive'}`}>
                  <span className="permission-checkmark">✓</span>
                  <span className="permission-text">PAYS FEES</span>
                  </div>
                </div>
              </div>

            {/* School Notices Section */}
            <div className="parent-notices-card">
              <div className="section-orange-bar"></div>
              <div className="section-header children-header-flex">
                <h3 className="section-title">
                  School Notices
                  <span className="count-badge">({notices.length})</span>
                </h3>
                <button
                  type="button"
                  className="refresh-notices-btn"
                  onClick={fetchParentNotices}
                  disabled={noticesLoading || children.length === 0}
                >
                  {noticesLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
              {noticesLoading ? (
                <div className="notices-empty-state">
                  <p className="empty-state-message">Loading notices...</p>
                </div>
              ) : notices.length > 0 ? (
                <div className="parent-notices-list">
                  {notices.map((notice) => (
                    <div key={notice.id} className={`parent-notice-item ${getPriorityClass(notice.priority)}`}>
                      <div className="parent-notice-header">
                        <h4 className="parent-notice-title">{notice.title}</h4>
                        <span className="parent-notice-priority">
                          {getPriorityIcon(notice.priority)} {getPriorityLabel(notice.priority)}
                        </span>
                      </div>
                      <p className="parent-notice-content">{notice.content}</p>
                      <div className="parent-notice-meta">
                        <span>Posted: {formatNoticeDate(notice.created_at)}</span>
                        {notice.expiry_date && (
                          <span>Expires: {formatNoticeDate(notice.expiry_date)}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="notices-empty-state">
                  <p className="empty-state-message">No school notices available at the moment.</p>
                </div>
              )}
            </div>

            {/* Your Children Section */}
            <div className="children-section-card">
              <div className="section-orange-bar"></div>
              <div className="section-header children-header-flex">
                <h3 className="section-title">
                  Your Children
                  <span className="count-badge">({children.length})</span>
                </h3>
              </div>
              {children.length > 0 ? (
                <div className="children-list">
                    {children.map((child) => (
                    <div key={child.id} className="child-card-modern">
                      <div className="child-card-left">
                        <span className="child-name-modern">{child.first_name} {child.last_name}</span>
                      </div>
                      <div className="child-card-right">
                        <span className="child-grade">
                          {child.classes?.class_name || child.class_id} - Section {child.sections?.section_name || child.section_id}
                        </span>
                      </div>
                      </div>
                    ))}
                  </div>
              ) : (
                <div className="children-empty-state">
                  <p className="empty-state-message">No children added yet</p>
                </div>
              )}
            </div>
          </div>
        )}

         {activeTab === 'children' && (
           <div className="children-section">
             <div className="children-header">
               <h2>My Children</h2>
               <p>View information about all your children ({children.length})</p>
             </div>
             {loadingChildren ? (
               <div className="children-loading">
                 <div className="loading-spinner">Loading...</div>
                 <p>Loading children...</p>
               </div>
             ) : children.length > 0 ? (
              <div className="children-grid">
                {children.map((child) => (
                  <div key={child.id} className="child-card">
                    <div className="child-card-header">
                      <div className="child-avatar">
                        {child.first_name?.charAt(0).toUpperCase()}{child.last_name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="child-name-section">
                        <h3 className="child-full-name">
                          {child.first_name} {child.middle_name ? child.middle_name + ' ' : ''}{child.last_name}
                        </h3>
                      </div>
                    </div>
                    
                    <div className="child-details">
                      <div className="child-detail-row">
                        <div className="detail-content">
                          <span className="detail-label">EMAIL</span>
                          <span className="detail-value">{child.email || 'N/A'}</span>
                        </div>
                      </div>
                      
                      <div className="child-detail-row">
                        <div className="detail-content">
                          <span className="detail-label">CLASS</span>
                          <span className="detail-value">{child.classes?.class_name || child.class_id || 'N/A'}</span>
                        </div>
                      </div>
                      
                      <div className="child-detail-row">
                        <div className="detail-content">
                          <span className="detail-label">SECTION</span>
                          <span className="detail-value">{child.sections?.section_name || child.section_id || 'N/A'}</span>
                        </div>
                      </div>
                      
                      {child.date_of_birth && (
                        <div className="child-detail-row">
                          <div className="detail-content">
                            <span className="detail-label">DATE OF BIRTH</span>
                            <span className="detail-value">
                              {new Date(child.date_of_birth).toLocaleDateString('en-GB', { 
                                day: '2-digit', 
                                month: '2-digit', 
                                year: 'numeric' 
                              })}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {child.gender && (
                        <div className="child-detail-row">
                          <div className="detail-content">
                            <span className="detail-label">GENDER</span>
                            <span className="detail-value">{child.gender || 'N/A'}</span>
                          </div>
                        </div>
                      )}
                      
                      {child.schools?.school_name && (
                        <div className="child-detail-row">
                          <div className="detail-content">
                            <span className="detail-label">SCHOOL</span>
                            <span className="detail-value">{child.schools.school_name}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-children">
                <h3>No Children Found</h3>
                <p>No children are currently registered under your account.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'homework' && (
          <div className="homework-section">
            {/* Page Header */}
            <div className="homework-header">
              <div className="homework-header-content">
                <div className="homework-header-title">
                  <span className="homework-header-icon">📝</span>
                  <h2>Children's Homework</h2>
                </div>
                <p className="homework-subtitle">Homework assignments for all your children</p>
              </div>
            </div>

            {/* Child Selector */}
            {renderChildSelector()}

            {/* Date Navigation */}
            <div className="homework-date-navigation">
              <button 
                className="nav-btn prev-btn"
                onClick={() => navigateHomeworkDate('prev')}
                disabled={loadingHomework}
              >
                ← Previous Day
              </button>
              <div className="current-date">
                <h3>{new Date(homeworkDate).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}</h3>
                <p className="date-info">
                  {homeworkDate === new Date().toISOString().split('T')[0] ? 'Today' : 
                   homeworkDate < new Date().toISOString().split('T')[0] ? 'Previous Day' : 'Future Day'}
                </p>
              </div>
              <button 
                className="nav-btn next-btn"
                onClick={() => navigateHomeworkDate('next')}
                disabled={loadingHomework}
              >
                Next Day →
              </button>
            </div>

            {loadingHomework ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading homework...</p>
              </div>
            ) : homeworkData.length === 0 ? (
              <div className="no-homework">
                <div className="no-homework-icon">📚</div>
                <h3>No Homework Available</h3>
                <p>No homework assignments found for your children.</p>
              </div>
            ) : filteredHomework.length === 0 ? (
              <div className="no-homework">
                <div className="no-homework-icon">📅</div>
                <h3>No homework for this day</h3>
                <p>Check other dates or come back later</p>
              </div>
            ) : (
              <div className="homework-grid">
                {filteredHomework.map((homework) => {
                  const completionKey = `${homework.id}-${homework.child_id}`;
                  const completion = homeworkCompletionStatus[completionKey];
                  const isCompleted = completion?.is_completed || false;
                  
                  return (
                    <div key={`${homework.id}-${homework.child_id}`} className={`homework-card ${isCompleted ? 'completed-homework' : ''}`}>
                      <div className="homework-header-card">
                        <div className="homework-title-section">
                          <div>
                            <h4>{homework.title}</h4>
                            {homework.child_name && (
                              <p className="child-name-badge">{homework.child_name}</p>
                            )}
                          </div>
                          {isCompleted && (
                            <span className="completion-badge completed-badge">
                              ✅ Completed
                            </span>
                          )}
                          {!isCompleted && completion && (
                            <span className="completion-badge pending-badge">
                              ⏳ Not Completed
                            </span>
                          )}
                        </div>
                        <span className="homework-subject">
                          <span className="subject-icon">📖</span>
                          {homework.subject}
                        </span>
                      </div>
                      
                      <div className="homework-details">
                        <div className="homework-description">
                          <p>{homework.description}</p>
                        </div>
                        
                        {isCompleted && completion.completed_at && (
                          <div className="completion-info">
                            <span className="completion-date">
                              ✓ Completed on: {new Date(completion.completed_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </span>
                          </div>
                        )}
                        
                        <div className="homework-dates">
                          <div className="assigned-date">
                            <span className="date-icon">📅</span>
                            <span>Assigned: {new Date(homework.assigned_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                          </div>
                          {homework.due_date && (
                            <div className="due-date">
                              <span className="date-icon">⏰</span>
                              <span>Due: {new Date(homework.due_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'attendance' && (
          <div className="attendance-section">
            <div className="attendance-header-card">
              <h2>Attendance Records</h2>
              <p>View attendance records for your children</p>
            </div>

            {/* Child Selector */}
            {renderChildSelector()}

            {/* Month Navigation */}
            <div className="month-navigation">
              <button
                className="nav-btn"
                onClick={() => {
                  if (selectedMonth === 0) {
                    setSelectedMonth(11);
                    setSelectedYear(selectedYear - 1);
                  } else {
                    setSelectedMonth(selectedMonth - 1);
                  }
                }}
              >
                <span className="nav-arrow">←</span>
                <span className="nav-text">Previous</span>
              </button>
              <div className="current-month">
                <h3>
                  {new Date(selectedYear, selectedMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h3>
              </div>
              <button
                className="nav-btn"
                onClick={() => {
                  if (selectedMonth === 11) {
                    setSelectedMonth(0);
                    setSelectedYear(selectedYear + 1);
                  } else {
                    setSelectedMonth(selectedMonth + 1);
                  }
                }}
              >
                <span className="nav-text">Next</span>
                <span className="nav-arrow">→</span>
              </button>
            </div>

            {loadingAttendance ? (
              <div className="children-loading">
                <div className="loading-spinner">Loading...</div>
                <p>Loading attendance records...</p>
              </div>
            ) : attendanceData.length === 0 ? (
              <div className="no-attendance">
                <div className="no-homework-icon">📅</div>
                <h3>No Attendance Records</h3>
                <p>No attendance records found for {new Date(selectedYear, selectedMonth).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
              </div>
            ) : (
              <>
                {/* Group attendance by child */}
                {(() => {
                  // Filter by selected child if one is selected
                  let filteredAttendanceData = attendanceData;
                  if (selectedChild) {
                    const selectedChildId = String(selectedChild);
                    filteredAttendanceData = attendanceData.filter(record => {
                      const childId = String(record.child_id || record.student_id);
                      return childId === selectedChildId;
                    });
                  }
                  
                  const groupedByChild = {};
                  filteredAttendanceData.forEach(record => {
                    const childId = record.child_id || record.student_id;
                    if (!groupedByChild[childId]) {
                      groupedByChild[childId] = {
                        child: record.child,
                        child_name: record.child_name,
                        child_class: record.child_class,
                        child_section: record.child_section,
                        records: []
                      };
                    }
                    groupedByChild[childId].records.push(record);
                  });

                  // Calculate stats for each child
                  const getChildStats = (records) => {
                    const total = records.length;
                    const present = records.filter(r => r.status === 'present').length;
                    const absent = records.filter(r => r.status === 'absent').length;
                    const late = records.filter(r => r.status === 'late').length;
                    const percentage = total > 0 ? Math.round((present / total) * 100) : 0;
                    return { total, present, absent, late, percentage };
                  };

                  return Object.values(groupedByChild).map((group, idx) => {
                    const stats = getChildStats(group.records);
                    return (
                      <div key={idx} style={{ marginBottom: '32px' }}>
                        <div className="attendance-child-header" style={{
                          background: '#FF8B3D',
                          color: '#FFFFFF',
                          padding: '20px',
                          borderRadius: '12px',
                          marginBottom: '20px'
                        }}>
                          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700 }}>
                            {group.child_name} - {group.child_class} ({group.child_section})
                          </h3>
                          <div style={{ display: 'flex', gap: '20px', marginTop: '12px', fontSize: '14px' }}>
                            <span>📊 Total: {stats.total}</span>
                            <span>✅ Present: {stats.present}</span>
                            <span>❌ Absent: {stats.absent}</span>
                            {stats.late > 0 && <span>⏰ Late: {stats.late}</span>}
                            <span>📈 {stats.percentage}%</span>
                          </div>
                        </div>

                        <div className="attendance-records-list" style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                          gap: '16px'
                        }}>
                          {group.records.map((record) => (
                            <div key={record.id} className="attendance-record-card" style={{
                              background: '#FFFFFF',
                              borderRadius: '12px',
                              padding: '20px',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                              borderLeft: `4px solid ${
                                record.status === 'present' ? '#10B981' :
                                record.status === 'absent' ? '#EF4444' : '#F59E0B'
                              }`
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                <div style={{ fontSize: '16px', fontWeight: 600, color: '#1F2937' }}>
                                  {new Date(record.attendance_date).toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                  })}
                                </div>
                                <span style={{
                                  padding: '4px 12px',
                                  borderRadius: '20px',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  background: record.status === 'present' ? '#D1FAE5' :
                                    record.status === 'absent' ? '#FEE2E2' : '#FEF3C7',
                                  color: record.status === 'present' ? '#065F46' :
                                    record.status === 'absent' ? '#991B1B' : '#92400E'
                                }}>
                                  {record.status.toUpperCase()}
                                </span>
                              </div>
                              {record.teacher && (
                                <div style={{ fontSize: '14px', color: '#6B7280', marginTop: '8px' }}>
                                  <strong>Teacher:</strong> {record.teacher.first_name} {record.teacher.last_name}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  });
                })()}
              </>
            )}
          </div>
        )}

        {activeTab === 'fees' && (
          <div className="fees-section">
            {/* Page Header */}
            <div className="fees-page-header">
              <span className="fees-header-icon">💰</span>
              <h1 className="fees-page-title">Fee Details</h1>
              <p className="fees-page-subtitle">Fees due for your children</p>
            </div>

            {/* Child Selector */}
            {renderChildSelector()}

            {feesLoading ? (
              <div className="fees-loading">
                <div className="loading-spinner">⏳</div>
                <p>Loading fee information...</p>
              </div>
            ) : Object.keys(childrenFees).length === 0 ? (
              <div className="no-fees">
                <div className="no-fees-icon">💰</div>
                <h3>No Fees Due</h3>
                <p>Your children don't have any fees due at the moment.</p>
                <p style={{fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem'}}>
                  Check back later or contact the school office for more information.
                </p>
              </div>
            ) : (
              <div className="children-fees-container">
                {(selectedChild ? children.filter(c => String(c.id) === String(selectedChild)) : children).map(child => {
                  const childFees = childrenFees[child.id] || [];
                  if (childFees.length === 0) return null;
                  
                  const childName = `${child.first_name} ${child.middle_name ? child.middle_name + ' ' : ''}${child.last_name}`;
                  const childClass = child.classes?.class_name || 'N/A';
                  const childSection = child.section_id || 'N/A';
                  
                  return (
                    <div key={child.id} className="child-fees-section">
                      <div className="child-fees-header">
                        <h2 className="child-fees-name">{childName}</h2>
                        <p className="child-fees-meta">{childClass} - {childSection}</p>
                      </div>
                      
                      <div className="fee-container">
                        {childFees.map((fee, index) => {
                          const monthParts = fee.month ? fee.month.split(' ') : ['', ''];
                          const monthName = monthParts[0] || '';
                          const year = monthParts[1] || new Date().getFullYear().toString();
                          
                          return (
                            <div key={index} className="fee-card-nexy">
                              {/* Left Sidebar */}
                              <div className="fee-sidebar">
                                <div className="fee-month-display">
                                  <h3 className="fee-month-name">{monthName}</h3>
                                  <div className="fee-year">{year}</div>
                                </div>
                                <div className="fee-type-badge-nexy">MONTHLY</div>
                                <div className={`fee-status-badge-nexy ${fee.isPaid ? 'fee-status-paid' : 'fee-status-due'}`}>
                                  {fee.isPaid ? 'PAID' : 'DUE NOW'}
                                </div>
                                <div className="fee-sidebar-arrow"></div>
                              </div>
                              
                              {/* Right Content Section */}
                              <div className="fee-content-nexy">
                                {/* Amount Display Card */}
                                <div className="fee-amount-card">
                                  <div className="fee-currency-label">{fee.currency || 'PKR'}</div>
                                  <div className="fee-amount-value">{fee.feeAmount?.toLocaleString() || '0'}</div>
                                </div>
                                
                                {/* Details Section */}
                                <div className="fee-details-nexy">
                                  <div className="fee-detail-row">
                                    <span className="fee-detail-label">Due:</span>
                                    <span className="fee-detail-value">
                                      <span className="fee-calendar-icon">📅</span>
                                      {fee.dueDate ? fee.dueDate.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'schedule' && (
          <div className="schedule-section-modern">
            {/* Child Selector */}
            {renderChildSelector()}
            
            {loadingSchedule ? (
              <div className="schedule-loading">
                <div className="loading-spinner">⏳</div>
                <p>Loading schedules...</p>
              </div>
            ) : Object.keys(scheduleData).length === 0 ? (
              <div className="no-schedule">
                <div className="no-schedule-icon">📅</div>
                <h3>No Schedules Available</h3>
                <p>No class schedules found for your children.</p>
              </div>
            ) : (
              Object.values(scheduleData)
                .filter(scheduleInfo => {
                  if (!selectedChild) return true;
                  return String(scheduleInfo.child?.id) === String(selectedChild);
                })
                .map((scheduleInfo, idx) => {
                const { child, scheduleObject } = scheduleInfo;
                const periods = getPeriods(scheduleObject);
                
                return (
                  <div key={idx} style={{ marginBottom: '48px' }}>
                    {/* Page Title Section */}
                    <div className="schedule-title-card">
                      <div className="schedule-icon-large">📅</div>
                      <h2 className="schedule-title">Class Schedule</h2>
                      <p className="schedule-subtitle">
                        {child.first_name} {child.middle_name ? child.middle_name + ' ' : ''}{child.last_name} - {child.classes?.class_name || 'N/A'} ({child.sections?.section_name || child.section_id || 'N/A'})
                      </p>
                    </div>

                    {/* School Timings Card */}
                    {schoolTimings[child.id] && (
                      <div className="timings-card-modern">
                        <div className="timings-header">
                          <span className="timings-icon">🕐</span>
                          <h3 className="timings-title">School Timings</h3>
                        </div>
                        <div className="timings-info-grid">
                          <div className="timing-info-item">
                            <div className="timing-label-modern">School Hours</div>
                            <div className="timing-value-modern">
                              {(() => {
                                const startTime = schoolTimings[child.id].start_time || '08:00';
                                const endTime = schoolTimings[child.id].end_time || '15:00';
                                return `${formatTime(startTime)} - ${formatTime(endTime)}`;
                              })()}
                            </div>
                          </div>
                          <div className="timing-info-item">
                            <div className="timing-label-modern">Period Duration</div>
                            <div className="timing-value-modern">{schoolTimings[child.id].period_time || 45} minutes</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Semester Schedules */}
                    {scheduleInfo.schedulesBySemester && Object.keys(scheduleInfo.schedulesBySemester).length > 0 ? (
                      <div className="semester-schedules-container">
                        {Object.keys(scheduleInfo.schedulesBySemester).map((semester, semIndex) => {
                          const semesterData = scheduleInfo.schedulesBySemester[semester];
                          const semesterSchedule = semesterData.schedule;
                          
                          // Get working days and periods for this semester's schedule
                          const getWorkingDaysForSemester = () => {
                            const days = new Set();
                            Object.keys(semesterSchedule).forEach(key => {
                              const [day] = key.split('_');
                              days.add(day);
                            });
                            const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                            return dayOrder.filter(day => days.has(day));
                          };
                          
                          const getPeriodsForSemester = () => {
                            const periods = new Set();
                            Object.keys(semesterSchedule).forEach(key => {
                              const [day, period] = key.split('_');
                              periods.add(parseInt(period));
                            });
                            return Array.from(periods).sort((a, b) => a - b);
                          };
                          
                          const workingDays = getWorkingDaysForSemester();
                          const periods = getPeriodsForSemester();
                          const childTimings = schoolTimings[child.id];
                          
                          return (
                            <div key={semester} className={`semester-schedule-section ${semIndex === 0 ? 'first-semester' : ''}`}>
                              <div className="semester-header">
                                <h3 className="semester-title">
                                  📚 {semesterData.semester_name || semester}
                                  {semIndex === 0 && <span className="primary-badge">Primary</span>}
                                </h3>
                              </div>
                              <div className="schedule-table-container">
                                <table className="schedule-table">
                                  <thead>
                                    <tr className="schedule-table-header">
                                      <th className="period-header">Period</th>
                                      {workingDays.map(day => (
                                        <th key={day} className="day-header">
                                          {getDayName(day)}
                                        </th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(() => {
                                      const rows = [];
                                      
                                      periods.forEach(period => {
                                        const timing = childTimings ? calculatePeriodTimingsForPeriod(period, childTimings) : null;
                                        
                                        rows.push(
                                          <tr key={`period_${period}`} className="schedule-table-row">
                                            <td className="period-cell">
                                              <div className="period-label">Period {period}</div>
                                              {timing ? (
                                                <div className="period-timing">
                                                  <div className="timing-text">{timing.start} - {timing.end}</div>
                                                </div>
                                              ) : (
                                                <div className="period-timing" style={{color: '#dc3545', fontSize: '0.7rem', backgroundColor: '#f8d7da'}}>
                                                  No school timings set
                                                </div>
                                              )}
                                            </td>
                                            {workingDays.map(day => {
                                              const key = `${day}_${period}`;
                                              const scheduleItem = semesterSchedule[key] || {};
                                              
                                              return (
                                                <td key={day} className="schedule-cell">
                                                  {scheduleItem.subject_name ? (
                                                    <div className="schedule-assignment-display">
                                                      <div className="subject-display">
                                                        <strong>{scheduleItem.subject_name}</strong>
                                                      </div>
                                                      {scheduleItem.teacher_name && (
                                                        <div className="teacher-display">
                                                          <span className="teacher-name">{scheduleItem.teacher_name}</span>
                                                        </div>
                                                      )}
                                                    </div>
                                                  ) : (
                                                    <div className="empty-slot">
                                                      <span className="empty-text">Free Period</span>
                                                    </div>
                                                  )}
                                                </td>
                                              );
                                            })}
                                          </tr>
                                        );
                                        
                                        // Add break row if there's a break after this period
                                        if (timing && timing.breakAfter) {
                                          rows.push(
                                            <tr key={`break_after_${period}`} className="break-table-row">
                                              <td className="break-period-cell">
                                                <div className="break-label">☕ Break</div>
                                                <div className="break-timing">
                                                  {formatTime(timing.breakAfter.startTime)} - {formatTime(timing.breakAfter.endTime)}
                                                </div>
                                              </td>
                                              {workingDays.map(day => (
                                                <td key={day} className="break-cell">
                                                  <div className="break-display">
                                                    <span className="break-text">Break Time</span>
                                                  </div>
                                                </td>
                                              ))}
                                            </tr>
                                          );
                                        }
                                      });
                                      
                                      return rows;
                                    })()}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : scheduleObject && Object.keys(scheduleObject).length > 0 ? (
                      <div className="schedule-table-container">
                        <table className="schedule-table">
                          <thead>
                            <tr className="schedule-table-header">
                              <th className="period-header">Period</th>
                              {getWorkingDays().map(day => (
                                <th key={day} className="day-header">
                                  {getDayName(day)}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const rows = [];
                              const childTimings = schoolTimings[child.id];
                              
                              periods.forEach(period => {
                                const timing = childTimings ? calculatePeriodTimingsForPeriod(period, childTimings) : null;
                                
                                rows.push(
                                  <tr key={`period_${period}`} className="schedule-table-row">
                                    <td className="period-cell">
                                      <div className="period-label">Period {period}</div>
                                      {timing ? (
                                        <div className="period-timing">
                                          <div className="timing-text">{timing.start} - {timing.end}</div>
                                        </div>
                                      ) : (
                                        <div className="period-timing" style={{color: '#dc3545', fontSize: '0.7rem', backgroundColor: '#f8d7da'}}>
                                          No school timings set
                                        </div>
                                      )}
                                    </td>
                                    {getWorkingDays().map(day => {
                                      const key = `${day}_${period}`;
                                      const scheduleItem = scheduleObject[key] || {};
                                      
                                      return (
                                        <td key={day} className="schedule-cell">
                                          {scheduleItem.subject_name ? (
                                            <div className="schedule-assignment-display">
                                              <div className="subject-display">
                                                <strong>{scheduleItem.subject_name}</strong>
                                              </div>
                                              {scheduleItem.teacher_name && (
                                                <div className="teacher-display">
                                                  <span className="teacher-name">{scheduleItem.teacher_name}</span>
                                                </div>
                                              )}
                                            </div>
                                          ) : (
                                            <div className="empty-slot">
                                              <span className="empty-text">Free Period</span>
                                            </div>
                                          )}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                                
                                // Add break row if there's a break after this period
                                if (timing && timing.breakAfter) {
                                  rows.push(
                                    <tr key={`break_after_${period}`} className="break-table-row">
                                      <td className="break-period-cell">
                                        <div className="break-label">☕ Break</div>
                                        <div className="break-timing">
                                          {formatTime(timing.breakAfter.startTime)} - {formatTime(timing.breakAfter.endTime)}
                                        </div>
                                      </td>
                                      {getWorkingDays().map(day => (
                                        <td key={day} className="break-cell">
                                          <div className="break-display">
                                            <span className="break-text">Break Time</span>
                                          </div>
                                        </td>
                                      ))}
                                    </tr>
                                  );
                                }
                              });
                              
                              return rows;
                            })()}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="no-schedule">
                        <div className="no-schedule-icon">📅</div>
                        <h3>No Schedule Available</h3>
                        <p>Class schedule has not been set up yet for this child.</p>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {activeTab === 'exam-schedule' && (
          <div className="exam-schedule-section">
            {/* Page Header */}
            <div className="exam-schedule-header">
              <div className="exam-schedule-header-content">
                <div className="exam-schedule-header-icon">📅</div>
                <div className="exam-schedule-header-title">
                  <h2>Tests & Exams</h2>
                </div>
                <p className="exam-schedule-subtitle">Upcoming exams, tests, and holidays for your children</p>
              </div>
            </div>

            {/* Child Selector */}
            {renderChildSelector()}

            {examScheduleLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading exam schedule...</p>
              </div>
            ) : examScheduleData.length === 0 ? (
              <div className="no-exams">
                <div className="no-exams-icon">📅</div>
                <h3>No upcoming exams scheduled</h3>
                <p>There are no exams or tests scheduled for your children yet.</p>
              </div>
            ) : (
              <div className="exam-schedule-grid">
                {examScheduleData
                  .filter(exam => {
                    if (!selectedChild) return true;
                    return String(exam.child_id || exam.student_id) === String(selectedChild);
                  })
                  .map((exam, index) => (
                  <div key={`${exam.id}-${exam.child_id}-${index}`} className="exam-schedule-card">
                    <div className="exam-card-header">
                      <div className="exam-type-badge">
                        {exam.type === 'monthly_test' ? 'MONTHLY TEST' : 
                         exam.type === 'exam' ? 'EXAM' : 
                         exam.type === 'holiday' ? 'HOLIDAY' : 'TEST'}
                      </div>
                    </div>
                    
                    <div className="exam-card-content">
                      <div className="exam-child-info">
                        <span className="child-badge">{exam.child_name}</span>
                        <span className="class-badge">{exam.child_class} - Section {exam.child_section}</span>
                      </div>
                      
                      <h4 className="exam-title">{exam.title}</h4>
                      
                      <div className="exam-dates">
                        <span className="exam-date">
                          {exam.start_date === exam.end_date 
                            ? new Date(exam.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
                            : `${new Date(exam.start_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} - ${new Date(exam.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
                          }
                        </span>
                      </div>

                      {exam.subjects && exam.subjects.length > 0 && (
                        <div className="exam-subjects">
                          <div className="subjects-header">
                            <span className="subjects-icon">📚</span>
                            <span className="subjects-title">Subjects ({exam.subjects.length})</span>
                          </div>
                          <div className="subjects-list">
                            {exam.subjects.map((subject, subjectIndex) => (
                              <div key={subjectIndex} className="subject-item">
                                <span className="subject-name">{subject.name}</span>
                                <div className="subject-date-time-row">
                                  <span className="subject-date">
                                    <span className="date-icon">📅</span>
                                    {new Date(subject.date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                  </span>
                                  <span className="subject-time">
                                    <span className="time-icon">🕐</span>
                                    {subject.time}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'results' && (
          <div className="results-section">
            <div className="results-page-header">
              <div className="results-header-icon">📊</div>
              <h1 className="results-page-title">Exam Results</h1>
              <p className="results-page-subtitle">View published exam and test results for your children</p>
            </div>

            {/* Child Selector */}
            {renderChildSelector()}

            {resultsLoading ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading results...</p>
              </div>
            ) : examResults.length === 0 ? (
              <div className="no-results">
                <div className="no-results-icon">📊</div>
                <h3>No Results Available</h3>
                <p>Exam results will appear here once they are published by the school.</p>
              </div>
            ) : (
              <div className="results-list">
                {examResults
                  .filter(childResult => {
                    if (!selectedChild) return true;
                    return String(childResult.child?.id) === String(selectedChild);
                  })
                  .map((childResult, childIdx) => (
                  <div key={childIdx} className="child-results-section">
                    <div className="child-results-header">
                      <h3 className="child-name">
                        {childResult.child?.first_name} {childResult.child?.middle_name || ''} {childResult.child?.last_name}
                      </h3>
                      <p className="child-class">
                        {childResult.child?.classes?.class_name || 'N/A'} - Section {childResult.child?.section_id || 'N/A'}
                      </p>
                    </div>

                    {childResult.examGroups.map((examGroup, examIdx) => (
                      <div key={examIdx} className="result-card">
                        <div className="result-card-header">
                          <h3 className="result-exam-name">{examGroup.exam?.title || 'Unknown Exam'}</h3>
                          <div className="result-exam-meta">
                            <span className="result-exam-type-badge">
                              {examGroup.exam?.type === 'exam' ? 'Exam' : 
                               examGroup.exam?.type === 'monthly_test' ? 'Monthly Test' : 
                               examGroup.exam?.type === 'holiday' ? 'Holiday' : 'Test'}
                            </span>
                            {examGroup.exam?.start_date && (
                              <span className="result-exam-date">
                                {new Date(examGroup.exam.start_date).toLocaleDateString('en-GB', { 
                                  day: '2-digit', 
                                  month: 'short', 
                                  year: 'numeric' 
                                })}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="result-card-body">
                          <table className="results-table">
                            <thead>
                              <tr>
                                <th>Subject</th>
                                <th>Obtained Marks</th>
                                <th>Total Marks</th>
                                <th>Percentage</th>
                                <th>Grade</th>
                              </tr>
                            </thead>
                            <tbody>
                              {examGroup.marks.map((mark, markIdx) => (
                                <tr key={markIdx}>
                                  <td className="subject-cell">
                                    <strong>{mark.subject_name}</strong>
                                  </td>
                                  <td className="marks-cell">
                                    {mark.obtained_marks !== null && mark.obtained_marks !== undefined 
                                      ? parseFloat(mark.obtained_marks).toFixed(2)
                                      : '-'}
                                  </td>
                                  <td className="marks-cell">
                                    {mark.total_marks !== null && mark.total_marks !== undefined 
                                      ? parseFloat(mark.total_marks).toFixed(2)
                                      : '-'}
                                  </td>
                                  <td className="percentage-cell">
                                    {mark.percentage !== null && mark.percentage !== undefined 
                                      ? `${parseFloat(mark.percentage).toFixed(2)}%`
                                      : '-'}
                                  </td>
                                  <td className="grade-cell">
                                    {mark.grade ? (
                                      <span className={`grade-badge grade-${mark.grade.toUpperCase().charAt(0)}`}>
                                        {mark.grade}
                                      </span>
                                    ) : '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr className="total-row">
                                <td><strong>Total</strong></td>
                                <td>
                                  <strong>
                                    {examGroup.marks.reduce((sum, m) => {
                                      const obtained = parseFloat(m.obtained_marks) || 0;
                                      return sum + obtained;
                                    }, 0).toFixed(2)}
                                  </strong>
                                </td>
                                <td>
                                  <strong>
                                    {examGroup.marks.reduce((sum, m) => {
                                      const total = parseFloat(m.total_marks) || 0;
                                      return sum + total;
                                    }, 0).toFixed(2)}
                                  </strong>
                                </td>
                                <td>
                                  <strong>
                                    {(() => {
                                      const totalObtained = examGroup.marks.reduce((sum, m) => {
                                        return sum + (parseFloat(m.obtained_marks) || 0);
                                      }, 0);
                                      const totalMarks = examGroup.marks.reduce((sum, m) => {
                                        return sum + (parseFloat(m.total_marks) || 0);
                                      }, 0);
                                      return totalMarks > 0 ? ((totalObtained / totalMarks) * 100).toFixed(2) : '0.00';
                                    })()}%
                                  </strong>
                                </td>
                                <td></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                        <div className="result-card-action">
                          <button
                            onClick={() => {
                              setSelectedResultCard({
                                child: childResult.child,
                                exam: examGroup.exam,
                                marks: examGroup.marks
                              });
                              setShowResultCard(true);
                            }}
                            className="view-result-card-btn-main"
                            title="View Result Card"
                          >
                            View Result Card
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'chat' && (
          <div className="chat-section-modern">
            {/* Page Header */}
            <div className="chat-page-header">
              <div className="chat-header-icon">💬</div>
              <h1 className="chat-page-title">Chat with Teachers</h1>
              <p className="chat-page-subtitle">Communicate with your children's teachers</p>
            </div>

            {/* Child Selector */}
            {renderChildSelector()}

            {teachersLoading ? (
              <div className="chat-loading-container">
                <div className="loading-spinner">⏳</div>
                <p>Loading teachers...</p>
              </div>
            ) : teachers.length === 0 ? (
              <div className="chat-no-teachers">
                <div className="no-teachers-icon">👨‍🏫</div>
                <h3>No Teachers Found</h3>
                <p>No teachers are currently assigned to your children's classes.</p>
              </div>
            ) : (
              <div className="chat-main-container">
                {/* Left Sidebar - Teachers List */}
                <div className={`chat-sidebar ${!showSidebar ? 'hidden-mobile' : ''}`}>
                  <div className="chat-sidebar-header">
                    <h3 className="chat-sidebar-title">Your Teachers</h3>
                  </div>
                  <div className="chat-search-container">
                    <input
                      type="text"
                      className="chat-search-input"
                      placeholder="Search teachers..."
                      value={teacherSearchQuery}
                      onChange={(e) => setTeacherSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="chat-teachers-list">
                    {teachers
                      .filter(teacher => {
                        if (!teacherSearchQuery.trim()) return true;
                        const query = teacherSearchQuery.toLowerCase();
                        const name = `${teacher.first_name} ${teacher.last_name}`.toLowerCase();
                        const subjects = (teacher.subjects || []).join(' ').toLowerCase();
                        return name.includes(query) || subjects.includes(query);
                      })
                      .map(teacher => (
                        <div
                          key={teacher.id}
                          className={`chat-teacher-card ${selectedTeacher?.id === teacher.id ? 'selected' : ''}`}
                          onClick={() => {
                            setSelectedTeacher(teacher);
                            setShowSidebar(false);
                          }}
                        >
                          <div className="chat-teacher-avatar-wrapper">
                            <div className="chat-teacher-avatar">{teacher.isAdmin ? '🏫' : '👨‍🏫'}</div>
                            <div className="chat-online-indicator"></div>
                          </div>
                          <div className="chat-teacher-info">
                            <h4 className="chat-teacher-name">{teacher.first_name} {teacher.last_name}</h4>
                            {teacher.subjects && teacher.subjects.length > 0 && (
                              <p className="chat-teacher-subject">{teacher.subjects[0].toUpperCase()}</p>
                            )}
                            {teacher.children && teacher.children.length > 0 && (
                              <p className="chat-teacher-qualification">
                                Teaching: {teacher.children.map(c => c.name).join(', ')}
                              </p>
                            )}
                          </div>
                          {unreadCounts[teacher.id] > 0 && (
                            <div className="chat-unread-badge">{unreadCounts[teacher.id] > 99 ? '99+' : unreadCounts[teacher.id]}</div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>

                {/* Right Chat Area */}
                {selectedTeacher ? (
                  <div className="chat-window-modern">
                    <div className="chat-window-header-modern">
                      <button 
                        className="chat-back-button"
                        onClick={() => {
                          setShowSidebar(true);
                          setSelectedTeacher(null);
                        }}
                      >
                        ←
                      </button>
                      <div className="chat-header-avatar-wrapper">
                        <div className="chat-header-avatar">{selectedTeacher.isAdmin ? '🏫' : '👨‍🏫'}</div>
                        <div className="chat-online-indicator-small"></div>
                      </div>
                      <div className="chat-header-teacher-info">
                        <h3 className="chat-header-teacher-name">{selectedTeacher.first_name} {selectedTeacher.last_name}</h3>
                        {selectedTeacher.isAdmin && (
                          <p className="chat-header-teacher-subject">Administration</p>
                        )}
                        {selectedTeacher.subjects && selectedTeacher.subjects.length > 0 && (
                          <p className="chat-header-teacher-subject">{selectedTeacher.subjects[0]}</p>
                        )}
                      </div>
                    </div>

                    {chatLoading ? (
                      <div className="chat-loading-state">
                        <div className="loading-spinner">⏳</div>
                        <p>Loading chat...</p>
                      </div>
                    ) : (
                      <>
                        <div className="chat-messages-area">
                          {chatMessages.length === 0 ? (
                            <div className="chat-no-messages">
                              <p>No messages yet. Start the conversation!</p>
                            </div>
                          ) : (
                            <>
                              {chatMessages.map(message => {
                                const lastReadKey = selectedTeacher.isAdmin 
                                  ? `lastRead_${parentData.id}_admin`
                                  : `lastRead_${parentData.id}_${selectedTeacher.id}`;
                                const lastReadTimestamp = localStorage.getItem(lastReadKey);
                                const isUnread = (message.sender_type === 'teacher' || message.sender_type === 'school') && 
                                  (!lastReadTimestamp || message.created_at > lastReadTimestamp);
                                
                                // Mark message as read when viewed
                                if (isUnread && (message.sender_type === 'teacher' || message.sender_type === 'school')) {
                                  markMessageAsRead(message.id, message);
                                }

                                const messageStatus = getMessageStatus(message);
                                
                                return (
                                  <div
                                    key={message.id}
                                    className={`chat-message ${message.sender_type === 'parent' && message.sender_id === (parentData.id?.toString() || parentData.email) ? 'sent' : 'received'} ${isUnread ? 'has-unread-indicator' : ''}`}
                                  >
                                    <div className="chat-message-content">
                                      {isUnread && <div className="unread-indicator-circle"></div>}
                                      <div className="chat-message-sender">
                                        {message.sender_type === 'parent' ? 'You' : selectedTeacher.isAdmin ? 'School Admin' : selectedTeacher.first_name}
                                      </div>
                                      {message.content && <div className="chat-message-text">{message.content}</div>}
                                      {message.media_urls && Array.isArray(message.media_urls) && message.media_urls.length > 0 && (
                                        <div className="chat-message-media" style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                          {message.media_urls.map((media, idx) => (
                                            <div key={idx}>
                                              {media.type && media.type.startsWith('image/') ? (
                                                <img
                                                  src={media.url}
                                                  alt={media.name || 'Image'}
                                                  style={{ maxWidth: '300px', maxHeight: '300px', borderRadius: '8px', cursor: 'pointer' }}
                                                  onClick={() => window.open(media.url, '_blank')}
                                                />
                                              ) : (
                                                <a
                                                  href={media.url}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    padding: '8px 12px',
                                                    background: '#F3F4F6',
                                                    borderRadius: '8px',
                                                    textDecoration: 'none',
                                                    color: '#1F2937',
                                                    maxWidth: '300px'
                                                  }}
                                                >
                                                  <span>📄</span>
                                                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {media.name || 'Document'}
                                                  </span>
                                                </a>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' }}>
                                        <div className="chat-message-time">
                                          {new Date(message.created_at).toLocaleString()}
                                        </div>
                                        {messageStatus && (
                                          <span style={{ 
                                            color: messageStatus.color, 
                                            fontSize: '14px', 
                                            marginLeft: '8px',
                                            fontWeight: '500'
                                          }} title={messageStatus.status === 'read' ? 'Read' : 'Delivered'}>
                                            {messageStatus.icon}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                              <div ref={chatMessagesEndRef} />
                            </>
                          )}
                        </div>

                        <div className="chat-input-area-modern">
                          {selectedFiles.length > 0 && (
                            <div className="chat-selected-files" style={{ padding: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                              {selectedFiles.map((file, index) => (
                                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', background: '#E5E7EB', borderRadius: '4px', fontSize: '12px' }}>
                                  <span>{file.name}</span>
                                  <button
                                    type="button"
                                    onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
                            <input
                              ref={fileInputRef}
                              type="file"
                              multiple
                              accept="image/*,.pdf,.doc,.docx,.txt"
                              onChange={handleFileSelect}
                              style={{ display: 'none' }}
                              id="chat-file-input-parent"
                            />
                            <label
                              htmlFor="chat-file-input-parent"
                              style={{ cursor: 'pointer', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F3F4F6', borderRadius: '8px', minWidth: '40px', flexShrink: 0 }}
                              title="Attach file"
                            >
                              📎
                            </label>
                            <input
                              type="text"
                              value={newMessage}
                              onChange={(e) => setNewMessage(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                              placeholder="Type your message..."
                              className="chat-input-modern"
                              style={{ flex: 1, minWidth: 0, width: '100%' }}
                            />
                            <button
                              onClick={sendMessage}
                              className="chat-send-button"
                              disabled={(!newMessage.trim() && selectedFiles.length === 0) || uploadingFiles}
                            >
                              {uploadingFiles ? '⏳' : 'Send'}
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="chat-empty-state">
                    <div className="chat-empty-icon">💬</div>
                    <h3 className="chat-empty-title">Select a Teacher</h3>
                    <p className="chat-empty-message">Choose a teacher from the list to start a conversation</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === 'transport' && (
          <div className="transport-section">
            {/* Child Selector */}
            {renderChildSelector()}
            
            {transportLoading ? (
              <div className="transport-loading">
                <div className="loading-spinner"></div>
                <p>Loading transport information...</p>
              </div>
            ) : children.length === 0 ? (
              <div className="transport-no-children">
                <div className="transport-icon">👨‍👩‍👧‍👦</div>
                <h2>No Children Found</h2>
                <p>No children are registered under your account.</p>
              </div>
            ) : (
              <div className="transport-content">
                <div className="transport-header">
                  <div className="transport-header-icon">🚌</div>
                  <div className="transport-header-info">
                    <h1>Transport Information</h1>
                    <p>View transport details and live bus locations for your children</p>
                  </div>
                </div>

                <div className="children-transport-list">
                  {(selectedChild ? children.filter(c => String(c.id) === String(selectedChild)) : children).map((child) => {
                    const transportData = childrenTransportData[child.id];
                    const busLocation = busLocations[child.id];
                    const childName = `${child.first_name} ${child.middle_name ? child.middle_name + ' ' : ''}${child.last_name}`;

                    return (
                      <div key={child.id} className="child-transport-card">
                        <div className="child-transport-header">
                          <h3>{childName}</h3>
                          {!transportData ? (
                            <span className="transport-status-badge inactive">Transport Not Enabled</span>
                          ) : (
                            <span className="transport-status-badge active">Transport Enabled</span>
                          )}
                        </div>

                        {!transportData ? (
                          <div className="transport-not-enabled-message">
                            <p>Transport service is not enabled for {childName}.</p>
                          </div>
                        ) : (
                          <>
                            <div className="transport-details-card">
                              <h4>Route Information</h4>
                              <div className="transport-info-grid">
                                <div className="transport-info-item">
                                  <span className="transport-info-label">Route Name:</span>
                                  <span className="transport-info-value">
                                    {transportData.route_name || `Route #${transportData.id}`}
                                  </span>
                                </div>
                                <div className="transport-info-item">
                                  <span className="transport-info-label">Route Type:</span>
                                  <span className="transport-info-value">{transportData.route_type}</span>
                                </div>
                                {transportData.vehicle && (
                                  <>
                                    <div className="transport-info-item">
                                      <span className="transport-info-label">Bus Number:</span>
                                      <span className="transport-info-value">{transportData.vehicle.bus_number}</span>
                                    </div>
                                    {transportData.vehicle.driver_name && (
                                      <div className="transport-info-item">
                                        <span className="transport-info-label">Driver:</span>
                                        <span className="transport-info-value">{transportData.vehicle.driver_name}</span>
                                      </div>
                                    )}
                                    {transportData.seatNumber && (
                                      <div className="transport-info-item">
                                        <span className="transport-info-label">Seat Number:</span>
                                        <span className="transport-info-value" style={{ 
                                          fontSize: '18px', 
                                          fontWeight: '700', 
                                          color: '#1F2937' 
                                        }}>
                                          Seat {transportData.seatNumber}
                                        </span>
                                      </div>
                                    )}
                                  </>
                                )}
                                {transportData.pickupLocation && (
                                  <div className="transport-info-item">
                                    <span className="transport-info-label">Pickup Location:</span>
                                    <span className="transport-info-value">
                                      {transportData.pickupLocation.location_name}
                                      {transportData.pickupLocation.address && ` - ${transportData.pickupLocation.address}`}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Live Bus Location Map */}
                            {transportData.allPickupLocations && transportData.allPickupLocations.length > 0 && (
                              <div className="transport-map-container">
                                <h4>Live Bus Location</h4>
                                <BusLocationMap 
                                  busLocation={busLocation}
                                  pickupLocations={transportData.allPickupLocations}
                                  studentPickupLocation={transportData.pickupLocation}
                                />
                                {busLocation ? (
                                  <div className="bus-status">
                                    <span className="bus-status-indicator active"></span>
                                    <span>Bus is on route - Last updated: {new Date(busLocation.timestamp).toLocaleTimeString()}</span>
                                  </div>
                                ) : (
                                  <div className="bus-status">
                                    <span className="bus-status-indicator inactive"></span>
                                    <span>Bus location not available</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'meals' && (
          <div className="meals-section">
            {/* Page Header */}
            <div className="meals-page-header">
              <span className="meals-header-icon">🍽️</span>
              <h1 className="meals-page-title">School Meals</h1>
              <p className="meals-page-subtitle">View meals available at your children's schools</p>
            </div>

            {/* Child Selector */}
            {renderChildSelector()}

            {mealsLoading ? (
              <div className="meals-loading">
                <div className="loading-spinner">⏳</div>
                <p>Loading meals...</p>
              </div>
            ) : meals.length === 0 ? (
              <div className="no-meals">
                <div className="no-meals-icon">🍽️</div>
                <h3>No Meals Available</h3>
                <p>No meals have been added by the schools yet.</p>
                <p style={{fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem'}}>
                  Check back later or contact the school office for more information.
                </p>
              </div>
            ) : (
              <div className="meals-grid">
                {meals
                  .filter(meal => {
                    if (!selectedChild) return true;
                    return String(meal.child_id || meal.student_id) === String(selectedChild);
                  })
                  .map(meal => (
                  <div key={meal.id} className="meal-card">
                    <div className="meal-card-header">
                      <div className="meal-type-badge">
                        {meal.meal_type === 'Break' ? '☕' : meal.meal_type === 'Lunch' ? '🍽️' : '🍪'}
                        <span className="meal-type-text">{meal.meal_type}</span>
                      </div>
                      <div className={`meal-frequency-badge ${meal.frequency.toLowerCase()}`}>
                        {meal.frequency}
                      </div>
                    </div>
                    <div className="meal-card-content">
                      <h3 className="meal-name">{meal.meal_name}</h3>
                      <p className="meal-description">{meal.description}</p>
                      <div className="meal-footer">
                        <span className="meal-date">
                          📅 Added: {new Date(meal.created_at).toLocaleDateString('en-GB', { 
                            day: '2-digit', 
                            month: 'short', 
                            year: 'numeric' 
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Result Card Modal */}
      {showResultCard && selectedResultCard && (
        <div className="modal-overlay" onClick={() => setShowResultCard(false)}>
          <div className="result-card-modal" onClick={(e) => e.stopPropagation()}>
            <div className="result-card-header">
              <div className="result-card-school-info">
                <h1>REPORT CARD</h1>
                <p className="school-name">{selectedResultCard.child?.school_name || 'School Name'}</p>
              </div>
              <button className="modal-close-btn" onClick={() => setShowResultCard(false)}>
                ✕
              </button>
            </div>

            <div className="result-card-body">
              {/* Student Info Section */}
              <div className="result-card-student-info">
                <div className="student-info-row">
                  <div className="info-item">
                    <span className="info-label">Student Name:</span>
                    <span className="info-value">
                      {selectedResultCard.child?.first_name} {selectedResultCard.child?.middle_name || ''} {selectedResultCard.child?.last_name}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Student ID:</span>
                    <span className="info-value">{selectedResultCard.child?.student_id_no || selectedResultCard.child?.id || 'N/A'}</span>
                  </div>
                </div>
                <div className="student-info-row">
                  <div className="info-item">
                    <span className="info-label">Class:</span>
                    <span className="info-value">{selectedResultCard.child?.classes?.class_name || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Section:</span>
                    <span className="info-value">{selectedResultCard.child?.section_id || 'N/A'}</span>
                  </div>
                </div>
                <div className="student-info-row">
                  <div className="info-item">
                    <span className="info-label">Exam/Test:</span>
                    <span className="info-value">{selectedResultCard.exam?.title || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Type:</span>
                    <span className="info-value">
                      {selectedResultCard.exam?.type === 'exam' ? 'Exam' : 
                       selectedResultCard.exam?.type === 'monthly_test' ? 'Monthly Test' : 
                       selectedResultCard.exam?.type === 'holiday' ? 'Holiday' : 'Test'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Marks Table */}
              <div className="result-card-marks-table">
                <table className="result-card-table">
                  <thead>
                    <tr>
                      <th>Subject</th>
                      <th>Obtained Marks</th>
                      <th>Total Marks</th>
                      <th>Percentage</th>
                      <th>Grade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedResultCard.marks.map((mark, idx) => (
                      <tr key={idx}>
                        <td className="subject-cell">
                          <strong>{mark.subject_name}</strong>
                        </td>
                        <td className="marks-cell">
                          {mark.obtained_marks !== null && mark.obtained_marks !== undefined 
                            ? parseFloat(mark.obtained_marks).toFixed(2)
                            : '-'}
                        </td>
                        <td className="marks-cell">
                          {mark.total_marks !== null && mark.total_marks !== undefined 
                            ? parseFloat(mark.total_marks).toFixed(2)
                            : '-'}
                        </td>
                        <td className="percentage-cell">
                          {mark.percentage !== null && mark.percentage !== undefined 
                            ? `${parseFloat(mark.percentage).toFixed(2)}%`
                            : '-'}
                        </td>
                        <td className="grade-cell">
                          {mark.grade ? (
                            <span className={`grade-badge-large grade-${mark.grade.toUpperCase().charAt(0)}`}>
                              {mark.grade}
                            </span>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td><strong>Total</strong></td>
                      <td>
                        <strong>
                          {selectedResultCard.marks.reduce((sum, m) => {
                            const obtained = parseFloat(m.obtained_marks) || 0;
                            return sum + obtained;
                          }, 0).toFixed(2)}
                        </strong>
                      </td>
                      <td>
                        <strong>
                          {selectedResultCard.marks.reduce((sum, m) => {
                            const total = parseFloat(m.total_marks) || 0;
                            return sum + total;
                          }, 0).toFixed(2)}
                        </strong>
                      </td>
                      <td>
                        <strong>
                          {(() => {
                            const totalObtained = selectedResultCard.marks.reduce((sum, m) => {
                              return sum + (parseFloat(m.obtained_marks) || 0);
                            }, 0);
                            const totalMarks = selectedResultCard.marks.reduce((sum, m) => {
                              return sum + (parseFloat(m.total_marks) || 0);
                            }, 0);
                            return totalMarks > 0 ? ((totalObtained / totalMarks) * 100).toFixed(2) : '0.00';
                          })()}%
                        </strong>
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Remarks Section */}
              {selectedResultCard.marks.some(m => m.remarks) && (
                <div className="result-card-remarks">
                  <h3>Remarks</h3>
                  <div className="remarks-list">
                    {selectedResultCard.marks.filter(m => m.remarks).map((mark, idx) => (
                      <div key={idx} className="remark-item">
                        <strong>{mark.subject_name}:</strong> {mark.remarks}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="result-card-footer">
              <button className="modal-close-button" onClick={() => setShowResultCard(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'policies' && (
        <ViewPolicies studentData={selectedChild ? { school_id: selectedChild.school_id } : (children.length > 0 ? { school_id: children[0].school_id } : null)} />
      )}

      {activeTab === 'contact-us' && (
        <ViewContactUs 
          studentData={selectedChild ? { school_id: selectedChild.school_id } : (children.length > 0 ? { school_id: children[0].school_id } : null)} 
          userType="parent"
        />
      )}
    </div>
  );
}

// Bus Location Map Component
function BusLocationMap({ busLocation, pickupLocations, studentPickupLocation }) {
  const validLocations = pickupLocations.filter(loc => loc.latitude && loc.longitude);
  
  // Show map if we have bus location OR valid pickup locations
  if (validLocations.length === 0 && !busLocation) {
    return (
      <div className="map-placeholder">
        <p>Map coordinates not available for this route</p>
      </div>
    );
  }

  // Calculate center
  let centerLat, centerLng;
  if (busLocation) {
    // If we have bus location, center on it
    centerLat = busLocation.lat;
    centerLng = busLocation.lng;
    // If we also have pickup locations, average them with bus location
    if (validLocations.length > 0) {
      const allPoints = [...validLocations, busLocation];
      centerLat = allPoints.reduce((sum, loc) => sum + (loc.lat || parseFloat(loc.latitude)), 0) / allPoints.length;
      centerLng = allPoints.reduce((sum, loc) => sum + (loc.lng || parseFloat(loc.longitude)), 0) / allPoints.length;
    }
  } else if (validLocations.length > 0) {
    centerLat = validLocations.reduce((sum, loc) => sum + parseFloat(loc.latitude), 0) / validLocations.length;
    centerLng = validLocations.reduce((sum, loc) => sum + parseFloat(loc.longitude), 0) / validLocations.length;
  } else {
    // Fallback center
    centerLat = 33.5651;
    centerLng = 73.0169;
  }

  // Create route polyline
  const routeCoords = validLocations.map(loc => [
    parseFloat(loc.latitude),
    parseFloat(loc.longitude)
  ]);

  return (
    <MapContainer
      center={[centerLat, centerLng]}
      zoom={13}
      style={{ height: '400px', width: '100%', borderRadius: '12px', zIndex: 1 }}
      scrollWheelZoom={true}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      
      {/* Bus Location Marker */}
      {busLocation && (
        <Marker
          position={[busLocation.lat, busLocation.lng]}
          icon={getBusIcon()}
        >
          <Popup>
            <div style={{ textAlign: 'center' }}>
              <strong>🚌 Bus</strong>
              <div style={{ fontSize: '12px', marginTop: '4px', color: '#666' }}>
                Live Location
              </div>
            </div>
          </Popup>
        </Marker>
      )}
      
      {/* Pickup Location Markers */}
      {validLocations.map((location, index) => {
        const isStudentLocation = studentPickupLocation && location.id === studentPickupLocation.id;
        return (
          <Marker
            key={location.id}
            position={[parseFloat(location.latitude), parseFloat(location.longitude)]}
            icon={isStudentLocation ? getDefaultIcon() : undefined}
          >
            <Popup>
              <div style={{ textAlign: 'center' }}>
                <strong>{location.location_name}</strong>
                {location.address && (
                  <div style={{ fontSize: '12px', marginTop: '4px', color: '#666' }}>
                    {location.address}
                  </div>
                )}
                {isStudentLocation && (
                  <div style={{ fontSize: '11px', marginTop: '4px', color: '#FF8B3D', fontWeight: '600' }}>
                    🎯 Pickup Point
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        );
      })}
      
      {/* Route Polyline */}
      {routeCoords.length > 1 && (
        <Polyline
          positions={routeCoords}
          color="#FF8B3D"
          weight={4}
          opacity={0.7}
        />
      )}
    </MapContainer>
  );
}

export default ParentDashboard;


